import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { KeyRound, Plus, Trash2, RefreshCw, Loader2, CheckCircle2, AlertCircle, Activity, LogOut, Copy, Shield, Wifi, WifiOff, DatabaseZap, AlertTriangle, Radio, Database, TrendingUp, Clock, Infinity as InfinityIcon, Brain, Zap, Sparkles, Eye, X, ShieldCheck, ShieldAlert, FlaskConical, Sword, Beaker } from "lucide-react";
import BrainLabBacktestModal from "../components/BrainLabBacktestModal";
import AiPicksPanel from "../components/admin/AiPicksPanel";
import MonoliteHistoryPanel from "../components/admin/MonoliteHistoryPanel";
import UsersAdminPanel from "../components/admin/UsersAdminPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)", border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)", green: "#22C55E", red: "#EF4444", yellow: "#F59E0B", cyan: "#06B6D4" };
const ADMIN_SESSION_KEY = "bc_admin_session";

function getAdminSession() {
  try { return JSON.parse(localStorage.getItem(ADMIN_SESSION_KEY) || "null"); } catch { return null; }
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

// ─── API CALLS PANEL ──────────────────────────────────────────────────────────
function ApiCallsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/api-stats?admin_token=${token}`);
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    const id = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const maxDaily = Math.max(1, ...((data?.daily_series || []).map((d) => d.calls)));

  return (
    <div
      data-testid="api-calls-panel"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderTop: `2px solid ${C.cyan}`,
        marginTop: "1.5rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.85rem 1rem",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Radio size={13} style={{ color: C.cyan }} />
        <span
          style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: C.text,
          }}
        >
          API-Football · Consumo
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.65rem",
            color: C.muted,
            padding: "0.15rem 0.5rem",
            border: `1px solid ${C.border}`,
          }}
        >
          LIVE · auto-refresh 30s
        </span>
        <button
          data-testid="api-stats-refresh-btn"
          onClick={handleRefresh}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: C.muted,
            display: "flex",
            padding: "0.2rem",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = C.cyan)}
          onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
        >
          <RefreshCw
            size={13}
            style={{ animation: refreshing ? "spin 0.8s linear infinite" : "none" }}
          />
        </button>
      </div>

      {loading ? (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <Loader2
            size={20}
            style={{
              color: C.muted,
              animation: "spin 0.8s linear infinite",
              margin: "0 auto",
            }}
          />
        </div>
      ) : !data ? (
        <div style={{ padding: "1.5rem", textAlign: "center" }}>
          <p
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.8rem",
              color: C.muted,
            }}
          >
            Nessun dato disponibile.
          </p>
        </div>
      ) : (
        <div style={{ padding: "1.25rem" }}>
          {/* 3 big numbers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: "0.75rem",
              marginBottom: "1.25rem",
            }}
          >
            {[
              {
                label: "Totale (sempre)",
                value: data.total_calls,
                color: C.cyan,
                testId: "api-total-calls",
              },
              {
                label: "Ultimi 7 giorni",
                value: data.last7_calls,
                color: C.green,
                testId: "api-last7-calls",
              },
              {
                label: "Oggi",
                value: data.today_calls,
                color: C.yellow,
                testId: "api-today-calls",
              },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={s.testId}
                style={{
                  background: `${s.color}06`,
                  border: `1px solid ${s.color}22`,
                  padding: "0.9rem 1rem",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontWeight: 700,
                    fontSize: "1.75rem",
                    color: s.color,
                    textShadow: `0 0 10px ${s.color}55`,
                  }}
                >
                  {s.value.toLocaleString("it-IT")}
                </div>
                <div
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: "0.68rem",
                    color: C.muted,
                    marginTop: "0.2rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Daily mini-bars (last 7 days) */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                marginBottom: "0.5rem",
              }}
            >
              <TrendingUp size={11} style={{ color: C.muted }} />
              <span
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  letterSpacing: "0.12em",
                  color: C.muted,
                  textTransform: "uppercase",
                }}
              >
                Chiamate per giorno (ultimi 7)
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "0.35rem",
                alignItems: "flex-end",
                height: 68,
              }}
            >
              {data.daily_series.map((d) => {
                const h = (d.calls / maxDaily) * 100;
                const today = new Date().toISOString().slice(0, 10) === d.date;
                const dayStr = new Date(d.date).toLocaleDateString("it-IT", {
                  day: "2-digit",
                  month: "short",
                });
                return (
                  <div
                    key={d.date}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem" }}
                    data-testid={`daily-bar-${d.date}`}
                  >
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.6rem",
                        color: today ? C.cyan : C.muted,
                      }}
                    >
                      {d.calls}
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: `${Math.max(h, 3)}%`,
                        background: today ? C.cyan : C.highlight,
                        boxShadow: today ? `0 0 10px ${C.cyan}60` : "none",
                        transition: "height 0.6s ease",
                      }}
                    />
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.58rem",
                        color: today ? C.text : C.muted,
                        textTransform: "uppercase",
                      }}
                    >
                      {dayStr}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top endpoints */}
          {data.top_endpoints && data.top_endpoints.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Database size={11} style={{ color: C.muted }} />
                <span
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    letterSpacing: "0.12em",
                    color: C.muted,
                    textTransform: "uppercase",
                  }}
                >
                  Top Endpoint
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                {data.top_endpoints.map((ep) => {
                  const topVal = data.top_endpoints[0].calls;
                  const pct = (ep.calls / topVal) * 100;
                  return (
                    <div
                      key={ep.endpoint}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "180px 1fr 60px",
                        alignItems: "center",
                        gap: "0.6rem",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: "0.7rem",
                          color: C.text,
                        }}
                      >
                        /{ep.endpoint}
                      </span>
                      <div
                        style={{
                          height: 5,
                          background: C.highlight,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            height: "100%",
                            width: `${pct}%`,
                            background: C.cyan,
                            boxShadow: `0 0 6px ${C.cyan}55`,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: "0.7rem",
                          color: C.muted,
                          textAlign: "right",
                        }}
                      >
                        {ep.calls.toLocaleString("it-IT")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer info */}
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              paddingTop: "0.75rem",
              borderTop: `1px solid ${C.highlight}`,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.65rem",
              color: C.muted,
              flexWrap: "wrap",
            }}
          >
            <span>
              Cache: <strong style={{ color: C.text }}>{data.cache_entries.toLocaleString("it-IT")}</strong> entries
            </span>
            {data.first_call_at && (
              <span>
                Prima chiamata:{" "}
                <strong style={{ color: C.text }}>{formatDate(data.first_call_at)}</strong>
              </span>
            )}
            {data.last_call_at && (
              <span>
                Ultima:{" "}
                <strong style={{ color: C.text }}>{formatDate(data.last_call_at)}</strong>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LOGIN FORM ───────────────────────────────────────────────────────────────
function AdminLoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/admin-login`, { password });
      const session = { token: res.data.token };
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
      onLogin(session);
    } catch {
      setError("Password non valida");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div style={{ position: "fixed", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.015) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <Shield size={16} style={{ color: C.yellow }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: "1.4rem", letterSpacing: "0.1em", color: C.text, textTransform: "uppercase" }}>
              BET <span style={{ color: C.green }}>CRACKER</span>
            </span>
          </div>
          <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: C.muted, letterSpacing: "0.2em", textTransform: "uppercase" }}>Admin Panel</p>
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, padding: "1.75rem" }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.7rem", color: C.muted, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: "0.5rem" }}>
                Password Admin
              </label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="••••••••"
                autoFocus
                style={{ width: "100%", boxSizing: "border-box", background: C.highlight, border: `1px solid ${error ? C.red : C.border}`, color: C.text, padding: "0.7rem 1rem", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.9rem", outline: "none" }}
              />
            </div>
            {error && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem", padding: "0.5rem 0.75rem", background: "rgba(239,68,68,0.08)", border: `1px solid rgba(239,68,68,0.2)` }}>
                <AlertCircle size={11} style={{ color: C.red }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: C.red }}>{error}</span>
              </div>
            )}
            <button data-testid="admin-login-btn" type="submit" disabled={loading}
              style={{ width: "100%", padding: "0.7rem", background: C.yellow, color: "#020202", border: "none", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", cursor: loading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
              {loading && <Loader2 size={13} style={{ animation: "spin 0.8s linear infinite" }} />}
              Accedi come Admin
            </button>
          </form>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── CODE ROW ─────────────────────────────────────────────────────────────────
function CodeRow({ item, onDelete, onRevoke, onExtend, token }) {
  const [deleting, setDeleting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [extending, setExtending] = useState(null); // null | "24h" | "permanent"
  const [copied, setCopied] = useState(false);
  // Re-render every 30s when there's an expiry, so the countdown ticks live.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!item.expires_at) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [item.expires_at]);

  const expiryInfo = (() => {
    const dur = item.duration || "permanent";
    if (dur === "permanent") return { label: "PERMANENTE", color: C.muted, sub: null };
    // Time-limited code that hasn't been activated yet — countdown starts on first access.
    if (!item.expires_at) {
      const durLbl = dur === "1h" ? "1 ORA" : "24 ORE";
      return { label: durLbl, color: C.yellow, sub: "Non attivato · countdown al 1° accesso" };
    }
    const ms = new Date(item.expires_at).getTime() - Date.now();
    if (ms <= 0) return { label: "SCADUTO", color: C.red, sub: "Verrà rimosso a breve" };
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const sub = h >= 24 ? `${Math.floor(h / 24)}g ${h % 24}h` : (h >= 1 ? `${h}h ${m}m` : `${m}m`);
    const color = ms < 3_600_000 ? C.red : (ms < 6 * 3_600_000 ? C.yellow : C.cyan);
    const durLbl = dur === "1h" ? "1 ORA" : "24 ORE";
    return { label: durLbl, color, sub: `Scade tra ${sub}` };
  })();

  const handleCopy = () => {
    copyToClipboard(item.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Eliminare il codice ${item.code}?`)) return;
    setDeleting(true);
    try { await onDelete(item.code); } finally { setDeleting(false); }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try { await onRevoke(item.code); } finally { setRevoking(false); }
  };

  const handleExtend = async (target) => {
    setExtending(target);
    try { await onExtend(item.code, target); } finally { setExtending(null); }
  };

  // Allowed transitions (mirrors backend _EXTEND_RULES): only upgrades.
  const currentDuration = item.duration || "permanent";
  const canExtendTo24h = currentDuration === "1h";
  const canExtendToPermanent = currentDuration === "1h" || currentDuration === "24h";

  return (
    <div data-testid={`code-row-${item.code}`}
      style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto auto", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", borderBottom: `1px solid ${C.highlight}` }}>

      {/* Active indicator */}
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.is_active ? C.green : "#3F3F46", flexShrink: 0 }} title={item.is_active ? "In uso" : "Non utilizzato"} />

      {/* Code + label */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.85rem", color: C.text, letterSpacing: "0.08em" }}>{item.code}</span>
          <button onClick={handleCopy} style={{ background: "none", border: "none", cursor: "pointer", color: copied ? C.green : C.muted, padding: 0, display: "flex" }}>
            {copied ? <CheckCircle2 size={11} /> : <Copy size={11} />}
          </button>
        </div>
        {item.label && <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", color: C.muted, marginTop: "0.1rem" }}>{item.label}</div>}
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: "#3F3F46", marginTop: "0.1rem" }}>
          Creato: {formatDate(item.created_at)}
          {item.last_seen && ` · Ultimo accesso: ${formatDate(item.last_seen)}`}
          {expiryInfo.sub && <span style={{ color: expiryInfo.color, marginLeft: "0.4rem" }}>· {expiryInfo.sub}</span>}
        </div>
      </div>

      {/* Duration badge */}
      <span data-testid={`duration-badge-${item.code}`}
        style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
          padding: "0.2rem 0.5rem",
          background: item.expires_at ? `${expiryInfo.color}11` : "var(--bc-panel-elev, rgba(255,255,255,0.03))",
          color: expiryInfo.color,
          border: `1px solid ${item.expires_at ? `${expiryInfo.color}55` : C.border}`,
          letterSpacing: "0.08em",
        }}>
        {expiryInfo.label}
      </span>

      {/* Status badge */}
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", padding: "0.2rem 0.5rem", background: item.is_active ? "rgba(34,197,94,0.08)" : "var(--bc-panel-elev, rgba(255,255,255,0.03))", color: item.is_active ? C.green : C.muted, border: `1px solid ${item.is_active ? "rgba(34,197,94,0.2)" : C.border}`, display: "flex", alignItems: "center", gap: "0.3rem" }}>
        {item.is_active ? <><Wifi size={9} /> ATTIVO</> : <><WifiOff size={9} /> LIBERO</>}
      </span>

      {/* Action buttons (kick / extend / delete) */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        {item.is_active && (
          <button data-testid={`revoke-${item.code}`} onClick={handleRevoke} disabled={revoking} title="Rimuovi dispositivo (l'utente dovrà reinserire il codice)"
            style={{ background: "none", border: `1px solid rgba(245,158,11,0.3)`, color: C.yellow, padding: "0.25rem 0.5rem", cursor: "pointer", fontSize: "0.65rem", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            {revoking ? <Loader2 size={10} style={{ animation: "spin 0.8s linear infinite" }} /> : <RefreshCw size={10} />}
            KICK
          </button>
        )}
        {canExtendTo24h && (
          <button data-testid={`extend-24h-${item.code}`}
            onClick={() => handleExtend("24h")} disabled={extending !== null}
            title="Estendi durata a 24 ore"
            style={{ background: "none", border: `1px solid ${C.cyan}55`, color: C.cyan, padding: "0.25rem 0.5rem", cursor: extending ? "wait" : "pointer", fontSize: "0.65rem", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            {extending === "24h" ? <Loader2 size={10} style={{ animation: "spin 0.8s linear infinite" }} /> : <Clock size={10} />}
            → 24H
          </button>
        )}
        {canExtendToPermanent && (
          <button data-testid={`extend-permanent-${item.code}`}
            onClick={() => handleExtend("permanent")} disabled={extending !== null}
            title="Estendi durata a permanente"
            style={{ background: "none", border: `1px solid ${C.green}55`, color: C.green, padding: "0.25rem 0.5rem", cursor: extending ? "wait" : "pointer", fontSize: "0.65rem", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            {extending === "permanent" ? <Loader2 size={10} style={{ animation: "spin 0.8s linear infinite" }} /> : <InfinityIcon size={10} />}
            → PERM
          </button>
        )}
        <button data-testid={`delete-${item.code}`} onClick={handleDelete} disabled={deleting} title="Elimina codice"
          style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: "0.25rem", display: "flex" }}
          onMouseEnter={(e) => e.currentTarget.style.color = C.red}
          onMouseLeave={(e) => e.currentTarget.style.color = C.muted}>
          {deleting ? <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : <Trash2 size={14} />}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN ADMIN PAGE ──────────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════
// SENSEI RECALIBRATION COMPONENTS
// ════════════════════════════════════════════════════════════════

const SENSEI_C = {
  magenta: "#FF6FD8",
  purple: "#B487FF",
  amber: "#F59E0B",
  green: "#22FFA3",
  red: "#EF4444",
  cyan: "#9DF6FF",
};

function SeverityBadge({ level }) {
  const map = {
    high: { bg: "rgba(239,68,68,0.12)", color: SENSEI_C.red, label: "ALTA" },
    medium: { bg: "rgba(245,158,11,0.12)", color: SENSEI_C.amber, label: "MEDIA" },
    low: { bg: "rgba(180,135,255,0.12)", color: SENSEI_C.purple, label: "BASSA" },
  };
  const s = map[String(level || "").toLowerCase()] || map.low;
  return (
    <span style={{
      padding: "0.15rem 0.5rem", background: s.bg, color: s.color,
      border: `1px solid ${s.color}40`, fontFamily: "'JetBrains Mono',monospace",
      fontSize: "0.6rem", letterSpacing: "0.1em", fontWeight: 700,
    }}>{s.label}</span>
  );
}

function VerifierBadge({ verdict, confidence }) {
  const map = {
    approved: { color: SENSEI_C.green, icon: <ShieldCheck size={11} />, label: "Approvato" },
    rejected: { color: SENSEI_C.red, icon: <X size={11} />, label: "Rifiutato" },
    needs_review: { color: SENSEI_C.amber, icon: <ShieldAlert size={11} />, label: "Da rivedere" },
  };
  const v = map[verdict] || map.needs_review;
  return (
    <span title={`Confidence verifier: ${((confidence || 0) * 100).toFixed(0)}%`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "0.3rem",
        padding: "0.18rem 0.5rem", color: v.color,
        background: `${v.color}14`, border: `1px solid ${v.color}40`,
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
        fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase",
      }}>
      {v.icon} {v.label}
    </span>
  );
}

function RiskChip({ level }) {
  const map = {
    low: { color: SENSEI_C.green, label: "Rischio basso" },
    medium: { color: SENSEI_C.amber, label: "Rischio medio" },
    high: { color: SENSEI_C.red, label: "Rischio alto" },
    unknown: { color: "#71717A", label: "Non valutato" },
  };
  const r = map[String(level || "unknown").toLowerCase()] || map.unknown;
  return (
    <span style={{
      color: r.color, fontFamily: "'JetBrains Mono',monospace",
      fontSize: "0.62rem", letterSpacing: "0.08em",
    }}>● {r.label}</span>
  );
}

function BacktestTable({ result, expanded, onToggle }) {
  const C = { text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)",
    border: "var(--bc-border, rgba(255,255,255,0.07))", panel: "var(--bc-panel-elev, #141416)" };
  const rows = (result?.results || []);
  const summary = result?.summary || {};

  const fmtDelta = (v, decimals = 2) => {
    if (v === null || v === undefined) return "—";
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(decimals)}`;
  };

  const colorFor = (verdict) => {
    if (verdict === "improved") return SENSEI_C.green;
    if (verdict === "worsened") return SENSEI_C.red;
    if (verdict === "neutral") return SENSEI_C.amber;
    return "#71717A"; // skipped/no_data
  };

  return (
    <div data-testid="sensei-backtest-table" style={{
      background: C.panel, border: `1px solid ${SENSEI_C.cyan}33`,
    }}>
      {/* Toggle header */}
      <button onClick={onToggle}
        style={{
          width: "100%", padding: "0.55rem 0.85rem", background: "transparent",
          border: "none", borderBottom: expanded ? `1px solid ${C.border}` : "none",
          color: C.text, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
          fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <FlaskConical size={11} style={{ color: SENSEI_C.cyan }} />
          Risultati backtest · {rows.length} azioni
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem", letterSpacing: 0, textTransform: "none" }}>
          <span style={{ color: SENSEI_C.green }}>✓ {summary.improved || 0}</span>
          <span style={{ color: SENSEI_C.red }}>✗ {summary.worsened || 0}</span>
          <span style={{ color: SENSEI_C.amber }}>≈ {summary.neutral || 0}</span>
          <span style={{ color: "#71717A" }}>— {summary.skipped || 0}</span>
          <span style={{ color: C.muted, marginLeft: "0.4rem" }}>
            {expanded ? "▲" : "▼"}
          </span>
        </span>
      </button>

      {expanded && (
        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem" }}>
            <thead>
              <tr style={{ background: "rgba(157,246,255,0.04)" }}>
                {["Mercato", "Scope", "Δpp", "N", "Hit % pre", "Hit % post", "ΔHit", "Brier pre", "Brier post", "ΔBrier", "Verdict"].map((h) => (
                  <th key={h} style={{
                    padding: "0.4rem 0.5rem", textAlign: "left", color: C.muted,
                    borderBottom: `1px solid ${C.border}`, fontSize: "0.6rem",
                    letterSpacing: "0.05em", whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.action_id || idx} style={{
                  borderBottom: idx < rows.length - 1 ? `1px solid ${C.border}` : "none",
                }}>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.text, whiteSpace: "nowrap" }}>{r.market}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.muted, fontSize: "0.6rem" }}>{r.scope}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: SENSEI_C.magenta, textAlign: "right" }}>{fmtDelta(r.delta_pp, 1)}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.muted, textAlign: "right" }}>{r.n_matches}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.text, textAlign: "right" }}>{r.hit_rate_before_pct !== null ? `${r.hit_rate_before_pct}%` : "—"}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.text, textAlign: "right" }}>{r.hit_rate_after_pct !== null ? `${r.hit_rate_after_pct}%` : "—"}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: r.hit_rate_delta_pp > 0 ? SENSEI_C.green : (r.hit_rate_delta_pp < 0 ? SENSEI_C.red : C.muted), textAlign: "right", fontWeight: 700 }}>
                    {fmtDelta(r.hit_rate_delta_pp, 2)}{r.hit_rate_delta_pp !== null ? "pp" : ""}
                  </td>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.muted, textAlign: "right" }}>{r.brier_before !== null ? r.brier_before : "—"}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: C.muted, textAlign: "right" }}>{r.brier_after !== null ? r.brier_after : "—"}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: r.brier_delta < 0 ? SENSEI_C.green : (r.brier_delta > 0 ? SENSEI_C.red : C.muted), textAlign: "right", fontWeight: 700 }}>
                    {fmtDelta(r.brier_delta, 4)}
                  </td>
                  <td style={{ padding: "0.4rem 0.5rem", color: colorFor(r.verdict), textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.6rem", fontWeight: 700 }}>
                    {r.verdict === "improved" ? "MIGLIORA" : r.verdict === "worsened" ? "PEGGIORA" : r.verdict === "neutral" ? "INVARIATO" : r.verdict === "no_data" ? "N/D" : "SKIP"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SenseiRecalibrationPanel({
  lastRun, overrides, running, loading, applyingActionId, phase, reverifying,
  backtesting, backtestResult, backtestExpanded,
  onRun, onAskApply, onAskDisableAll, onRefresh, onReverify, onBacktest, onToggleBacktest,
  onDeactivateOverride,
}) {
  const C = { surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)",
    border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)",
    muted: "var(--bc-sub, #71717A)" };

  const patterns = lastRun?.discovered_patterns || [];
  const actions = lastRun?.recommended_actions || [];
  const summary = lastRun?.executive_summary || "";
  const runId = lastRun?.run_id || lastRun?._id;
  const ds = lastRun?.dataset_summary || {};

  return (
    <div data-testid="sensei-recalibration-panel"
      style={{
        background: C.surface,
        border: `1px solid ${SENSEI_C.magenta}33`,
        padding: "1.25rem",
        marginBottom: "1.5rem",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 0 18px ${SENSEI_C.magenta}14`,
      }}>
      <div style={{ position: "absolute", top: 0, left: 0, height: "2px", width: "100%",
        background: `linear-gradient(90deg, transparent, ${SENSEI_C.magenta}, ${SENSEI_C.purple}, transparent)` }} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.85rem" }}>
        <Sword size={14} style={{ color: SENSEI_C.magenta, filter: `drop-shadow(0 0 6px ${SENSEI_C.magenta})` }} />
        <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
          Sensei AI · Ricalibrazione Profonda
        </span>
        <span style={{ marginLeft: "0.5rem", padding: "0.15rem 0.5rem", background: `${SENSEI_C.purple}14`, border: `1px solid ${SENSEI_C.purple}40`, color: SENSEI_C.purple, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem", letterSpacing: "0.1em" }}>
          CLAUDE SONNET 4.5 · AUTO-VERIFICA
        </span>
        <button onClick={onRefresh} title="Ricarica ultimo run"
          style={{ marginLeft: "auto", background: "none", border: "none", color: C.muted, cursor: "pointer", display: "flex", padding: "0.2rem" }}>
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Intro / spiegazione narrativa */}
      <p style={{ color: C.muted, fontSize: "0.78rem", lineHeight: 1.6, marginBottom: "0.85rem" }}>
        Sensei esegue un'analisi <strong style={{ color: C.text }}>chirurgica</strong> sull'intero storico di TUTTI i mercati: 1X2, Over/Under, BTTS, Doppia chance, <strong style={{ color: SENSEI_C.purple }}>Multigol casa/trasferta/totale</strong> (32 sotto-mercati totali). Identifica errori sistematici nelle <strong style={{ color: C.text }}>fasce medie 30-70%</strong> e nei singoli campionati.
        Claude auto-verifica ogni proposta. <strong style={{ color: SENSEI_C.cyan }}>Calibrazione consigliata: 1× al mese</strong> (~$0.30, ~3-8 min).
      </p>

      {/* CTA principale */}
      <button data-testid="sensei-run-btn"
        onClick={onRun} disabled={running}
        style={{
          width: "100%", padding: "0.8rem 1rem",
          background: running ? `${SENSEI_C.magenta}22` : SENSEI_C.magenta,
          color: running ? SENSEI_C.magenta : "#0A0A0A",
          border: `1.5px solid ${SENSEI_C.magenta}`,
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: "0.95rem",
          letterSpacing: "0.18em", textTransform: "uppercase",
          cursor: running ? "wait" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "0.55rem",
          boxShadow: running ? "none" : `0 0 18px ${SENSEI_C.magenta}66`,
          transition: "all 0.2s",
          marginBottom: phase ? "0.5rem" : "1rem",
        }}>
        {running
          ? <><Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} /> {phase || "Sensei in esecuzione…"}</>
          : <><Sparkles size={14} /> Avvia Sensei Deep Recalibration</>}
      </button>

      {/* Phase indicator (visible while running) */}
      {running && phase && (
        <p data-testid="sensei-phase-indicator" style={{ color: SENSEI_C.purple, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace", textAlign: "center", marginBottom: "1rem", letterSpacing: "0.05em" }}>
          ⚙ Analisi DEEP in corso (32 mercati · ~3-8 min) · puoi chiudere la pagina
        </p>
      )}

      {/* Empty state */}
      {!lastRun && !running && !loading && (
        <div style={{ padding: "1rem", background: C.highlight, border: `1px dashed ${C.border}`, fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: C.muted }}>
          Nessuna analisi disponibile. Avvia il primo run per scoprire i pattern di errore del software.
        </div>
      )}

      {lastRun && (
        <>
          {/* Executive summary + dataset stats */}
          <div style={{ background: C.highlight, border: `1px solid ${SENSEI_C.purple}22`, padding: "0.85rem 1rem", marginBottom: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
              <Eye size={12} style={{ color: SENSEI_C.purple }} />
              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.15em", textTransform: "uppercase", color: SENSEI_C.purple }}>
                Sintesi del Sensei
              </span>
            </div>
            {summary && (
              <p style={{ color: C.text, fontSize: "0.82rem", lineHeight: 1.6, fontFamily: "'DM Sans',sans-serif", marginBottom: "0.5rem" }}>
                {summary}
              </p>
            )}
            {/* Dataset mini-stats */}
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginTop: "0.6rem", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: C.muted }}>
              <span>📊 {ds.n_total?.toLocaleString("it-IT") || 0} match analizzati</span>
              <span>📅 ultimi {ds.window_days || 0} giorni</span>
              {ds.baseline_metrics && (
                <span>🎯 hit-rate 1X2 {ds.baseline_metrics.hit_rate_1x2_pct}% · Brier {ds.baseline_metrics.brier_1x2}</span>
              )}
            </div>
          </div>

          {/* Pattern Scoperti */}
          {patterns.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
                <FlaskConical size={12} style={{ color: SENSEI_C.cyan }} />
                <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
                  Pattern di errore scoperti · {patterns.length}
                </span>
              </div>
              <div style={{ background: C.highlight, border: `1px solid ${C.border}` }}>
                {patterns.map((p, idx) => (
                  <div key={p.id || idx} data-testid={`sensei-pattern-${p.id}`}
                    style={{ padding: "0.75rem 0.85rem", borderBottom: idx < patterns.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.35rem" }}>
                      <span style={{ color: C.text, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.4 }}>
                        {p.title}
                      </span>
                      <SeverityBadge level={p.severity} />
                    </div>
                    <div style={{ color: SENSEI_C.cyan, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", marginBottom: "0.4rem" }}>
                      📍 {p.scope}
                    </div>
                    <p style={{ color: C.muted, fontSize: "0.76rem", lineHeight: 1.5, fontFamily: "'DM Sans',sans-serif", marginBottom: "0.3rem" }}>
                      {p.issue_description}
                    </p>
                    {p.evidence && (
                      <p style={{ color: "#A1A1AA", fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.5, paddingLeft: "0.6rem", borderLeft: `2px solid ${SENSEI_C.purple}66` }}>
                        {p.evidence}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Azioni Raccomandate */}
          {actions.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
                <Sparkles size={12} style={{ color: SENSEI_C.magenta }} />
                <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
                  Azioni raccomandate · {actions.length}
                </span>
              </div>
              <div style={{ background: C.highlight, border: `1px solid ${C.border}` }}>
                {actions.map((a, idx) => {
                  const isApplied = a.applied;
                  const isRejected = a.verifier_verdict === "rejected";
                  const isApplying = applyingActionId === a.id;
                  return (
                    <div key={a.id || idx} data-testid={`sensei-action-${a.id}`}
                      style={{
                        padding: "0.85rem", borderBottom: idx < actions.length - 1 ? `1px solid ${C.border}` : "none",
                        opacity: isApplied ? 0.65 : 1,
                      }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.6rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
                        <span style={{ color: C.text, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.4, flex: "1 1 60%" }}>
                          {a.title}
                        </span>
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                          <VerifierBadge verdict={a.verifier_verdict} confidence={a.verifier_confidence} />
                        </div>
                      </div>

                      <p style={{ color: C.muted, fontSize: "0.76rem", lineHeight: 1.55, fontFamily: "'DM Sans',sans-serif", marginBottom: "0.5rem" }}>
                        {a.user_friendly_explanation}
                      </p>

                      {/* Metadata grid */}
                      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: C.muted, marginBottom: "0.5rem" }}>
                        <span>🎯 <strong style={{ color: C.text }}>{a.target_market}</strong> · {a.target_scope}</span>
                        <span style={{ color: SENSEI_C.magenta }}>
                          Δ {a.delta_pp > 0 ? "+" : ""}{a.delta_pp}pp
                        </span>
                        <span>📈 confidence {((a.confidence || 0) * 100).toFixed(0)}%</span>
                        <RiskChip level={a.collateral_risk} />
                      </div>

                      {a.expected_impact && (
                        <p style={{ color: SENSEI_C.cyan, fontSize: "0.72rem", fontFamily: "'DM Sans',sans-serif", fontStyle: "italic", marginBottom: "0.5rem" }}>
                          💡 {a.expected_impact}
                        </p>
                      )}

                      {a.verifier_comment && a.verifier_comment !== "Auto-verificato" && (
                        <p style={{ color: "#A1A1AA", fontSize: "0.7rem", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5, padding: "0.4rem 0.6rem", background: "rgba(157,246,255,0.05)", borderLeft: `2px solid ${SENSEI_C.cyan}66`, marginBottom: "0.5rem" }}>
                          {a.verifier_comment}
                        </p>
                      )}

                      {/* Action button */}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.4rem" }}>
                        {isApplied ? (
                          <span data-testid={`sensei-action-applied-${a.id}`} style={{ color: SENSEI_C.green, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <CheckCircle2 size={12} /> Applicato
                            {a.applied_at && (
                              <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", letterSpacing: 0, textTransform: "none", marginLeft: "0.4rem" }}>
                                · {formatDate(a.applied_at)}
                              </span>
                            )}
                          </span>
                        ) : isRejected ? (
                          <span style={{ color: SENSEI_C.red, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            ✗ Non applicabile (rifiutato dal verifier)
                          </span>
                        ) : (
                          <button data-testid={`sensei-apply-${a.id}`}
                            onClick={() => onAskApply(a, runId)} disabled={isApplying}
                            style={{
                              padding: "0.45rem 0.9rem",
                              background: isApplying ? `${SENSEI_C.magenta}22` : "transparent",
                              color: SENSEI_C.magenta,
                              border: `1px solid ${SENSEI_C.magenta}`,
                              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                              fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase",
                              cursor: isApplying ? "wait" : "pointer",
                              display: "flex", alignItems: "center", gap: "0.4rem",
                              transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => { if (!isApplying) e.currentTarget.style.background = `${SENSEI_C.magenta}14`; }}
                            onMouseLeave={(e) => { if (!isApplying) e.currentTarget.style.background = "transparent"; }}>
                            {isApplying ? <Loader2 size={11} style={{ animation: "spin 0.8s linear infinite" }} /> : <Sparkles size={11} />}
                            Applica
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Backtest section (visibile solo se c'è un run) */}
      {lastRun && (lastRun.recommended_actions || []).length > 0 && (
        <div data-testid="sensei-backtest-section" style={{ marginBottom: "1rem", marginTop: "0.25rem" }}>
          <button data-testid="sensei-backtest-btn"
            onClick={onBacktest} disabled={backtesting}
            style={{
              width: "100%", padding: "0.65rem 1rem",
              background: backtesting ? `${SENSEI_C.cyan}18` : "transparent",
              color: SENSEI_C.cyan,
              border: `1px dashed ${SENSEI_C.cyan}66`,
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.78rem", letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: backtesting ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
              marginBottom: backtestResult ? "0.5rem" : 0,
            }}>
            {backtesting
              ? <><Loader2 size={12} style={{ animation: "spin 0.8s linear infinite" }} /> Backtest in corso…</>
              : <><FlaskConical size={12} /> {backtestResult ? "Rilancia Backtest" : "Esegui Backtest (gratis, ~3s)"}</>}
          </button>

          {backtestResult && (
            <BacktestTable
              result={backtestResult}
              expanded={backtestExpanded}
              onToggle={onToggleBacktest}
            />
          )}
        </div>
      )}


      {/* Overrides Attivi */}
      <div data-testid="sensei-overrides-section" style={{ marginTop: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <ShieldCheck size={12} style={{ color: SENSEI_C.green }} />
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
            Override AI attivi · {overrides.length}
          </span>
          {overrides.length > 0 && (
            <button data-testid="sensei-disable-all-btn" onClick={onAskDisableAll}
              style={{ marginLeft: "auto", padding: "0.3rem 0.7rem", background: "transparent", color: SENSEI_C.red, border: `1px solid ${SENSEI_C.red}66`, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <X size={10} /> Disattiva tutti
            </button>
          )}
        </div>
        {overrides.length === 0 ? (
          <div style={{ padding: "0.85rem", background: "var(--bc-panel-elev, #141416)", border: `1px dashed ${C.border}`, fontFamily: "'DM Sans',sans-serif", fontSize: "0.74rem", color: C.muted }}>
            Nessun override attivo. Il modello sta usando solo la matematica pura.
          </div>
        ) : (
          <OverridesByMarket
            overrides={overrides}
            C={C}
            onDeactivateOverride={onDeactivateOverride}
          />
        )}
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
   OverridesByMarket — raggruppa gli override per famiglia di mercati
   (1X2, Over/Under, BTTS, Doppia chance, Multigol, Match DNA, Altri)
   con accordion + Diff Explorer inline per ciascun override.
   ──────────────────────────────────────────────────────────────────── */

const MARKET_FAMILIES = [
  { key: "1x2",       label: "1X2",            tests: (m) => ["home_win","away_win","draw","x1","x2","12","1x2"].some(k => m === k) },
  { key: "over_under",label: "Over / Under",   tests: (m) => m.startsWith("over") || m.startsWith("under") },
  { key: "btts",      label: "BTTS",           tests: (m) => m.includes("btts") || m === "gg" || m === "ng" },
  { key: "doppia",    label: "Doppia chance",  tests: (m) => ["1x","x2","12"].includes(m) || m.startsWith("dc_") },
  { key: "multigol",  label: "Multigol",       tests: (m) => m.startsWith("multigol") || m.includes("mg_") },
  { key: "dna",       label: "Match DNA",      tests: (m) => m.startsWith("match_dna") },
  { key: "other",     label: "Altri",          tests: () => true },
];

function _familyOf(market) {
  const m = (market || "").toLowerCase();
  for (const f of MARKET_FAMILIES) {
    if (f.key === "other") continue;
    if (f.tests(m)) return f;
  }
  return MARKET_FAMILIES[MARKET_FAMILIES.length - 1];
}

function OverridesByMarket({ overrides, C, onDeactivateOverride }) {
  const [openFamily, setOpenFamily] = useState(null);
  const [diffFor, setDiffFor] = useState(null);

  const groups = {};
  for (const o of overrides) {
    const fam = _familyOf(o.target_market);
    if (!groups[fam.key]) groups[fam.key] = { label: fam.label, items: [] };
    groups[fam.key].items.push(o);
  }
  const orderedKeys = MARKET_FAMILIES.map(f => f.key).filter(k => groups[k]);

  return (
    <div data-testid="overrides-by-market" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {orderedKeys.map((fk) => {
        const g = groups[fk];
        const isOpen = openFamily === fk;
        const totalDelta = g.items.reduce((s, o) => s + (Number(o.delta_pp) || 0), 0);
        return (
          <div key={fk} data-testid={`market-group-${fk}`}
            style={{ background: "var(--bc-panel-elev, #141416)", border: `1px solid ${SENSEI_C.green}22` }}>
            <button
              onClick={() => setOpenFamily(isOpen ? null : fk)}
              style={{
                width: "100%", background: "transparent", border: "none",
                padding: "0.65rem 0.85rem", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 10,
                color: C.text, textAlign: "left",
              }}>
              <span style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                fontSize: "0.82rem", letterSpacing: "0.08em", textTransform: "uppercase",
                color: C.text, flex: 1,
              }}>
                {g.label}
              </span>
              <span style={{
                padding: "0.15rem 0.5rem", background: `${SENSEI_C.green}1A`,
                border: `1px solid ${SENSEI_C.green}55`, color: SENSEI_C.green,
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
                letterSpacing: "0.08em",
              }}>
                {g.items.length} {g.items.length === 1 ? "OVERRIDE" : "OVERRIDES"}
              </span>
              <span style={{
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
                color: totalDelta > 0 ? SENSEI_C.green : totalDelta < 0 ? SENSEI_C.red : C.muted,
              }}>
                Σ {totalDelta > 0 ? "+" : ""}{totalDelta.toFixed(1)}pp
              </span>
              <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
                {isOpen ? "▼" : "▶"}
              </span>
            </button>
            {isOpen && (
              <div style={{ borderTop: `1px solid ${C.border}` }}>
                {g.items.map((o, idx) => (
                  <OverrideRow
                    key={o._id || idx}
                    override={o}
                    C={C}
                    isLast={idx === g.items.length - 1}
                    diffOpen={diffFor === (o._id || idx)}
                    onToggleDiff={() => setDiffFor(diffFor === (o._id || idx) ? null : (o._id || idx))}
                    onDeactivate={onDeactivateOverride}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function OverrideRow({ override: o, C, isLast, diffOpen, onToggleDiff, onDeactivate }) {
  const decision = o.last_decision || "NEW";
  const reinforce = o.reinforce_count || 0;
  const decisionColor =
    decision === "REINFORCE" ? SENSEI_C.green :
    decision === "REPLACE" ? SENSEI_C.purple :
    SENSEI_C.cyan;
  const samples = [40, 55, 70];
  const delta = Number(o.delta_pp || 0);
  const clamp = (v) => Math.max(0, Math.min(100, v));

  return (
    <div style={{
      padding: "0.55rem 0.85rem",
      borderBottom: isLast ? "none" : `1px solid ${C.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <span style={{
          padding: "0.12rem 0.45rem",
          background: `${decisionColor}1A`,
          border: `1px solid ${decisionColor}66`,
          color: decisionColor,
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.55rem", letterSpacing: "0.08em",
        }}>
          {decision}{reinforce > 0 && ` ×${reinforce + 1}`}
        </span>
        <span style={{ color: C.text, fontFamily: "'DM Sans',sans-serif", fontSize: "0.76rem", flex: "1 1 50%" }}>
          {o.title}
        </span>
        <span style={{
          color: delta > 0 ? SENSEI_C.green : delta < 0 ? SENSEI_C.red : C.muted,
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", fontWeight: 700,
        }}>
          {delta > 0 ? "+" : ""}{delta}pp
        </span>
        <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem" }}>
          {o.target_market} · {o.target_scope || "global"}
        </span>
        <button
          onClick={onToggleDiff}
          data-testid={`override-diff-toggle-${o._id}`}
          title="Mostra impatto su esempi"
          style={{
            background: diffOpen ? `${SENSEI_C.cyan}1A` : "transparent",
            border: `1px solid ${SENSEI_C.cyan}55`,
            color: SENSEI_C.cyan, padding: "0.2rem 0.55rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.55rem",
            cursor: "pointer", letterSpacing: "0.08em",
          }}
        >
          {diffOpen ? "× Chiudi" : "↔ Vedi impatto"}
        </button>
        {onDeactivate && (
          <button
            onClick={() => onDeactivate(o)}
            data-testid={`override-deactivate-${o._id}`}
            title="Disattiva questo singolo override"
            style={{
              background: "transparent", border: `1px solid ${SENSEI_C.red}44`,
              color: SENSEI_C.red, padding: "0.2rem 0.4rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.55rem",
              cursor: "pointer",
            }}
          >×</button>
        )}
      </div>

      {diffOpen && (
        <div data-testid={`override-diff-panel-${o._id}`} style={{
          marginTop: 8, padding: "0.7rem 0.8rem",
          background: "rgba(157,246,255,0.04)",
          border: `1px dashed ${SENSEI_C.cyan}44`,
        }}>
          <div style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem",
            color: SENSEI_C.cyan, letterSpacing: "0.14em",
            textTransform: "uppercase", marginBottom: 8,
          }}>
            Impatto su 3 probabilità di esempio · {o.target_market}
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {samples.map((before) => {
              const after = clamp(Math.round(before + delta));
              const diff = after - before;
              return (
                <div key={before} style={{
                  display: "grid", gridTemplateColumns: "55px 1fr 80px", gap: 10,
                  alignItems: "center",
                }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", color: C.muted,
                  }}>{before}%</span>
                  <div style={{ position: "relative", height: 16, background: "rgba(255,255,255,0.04)" }}>
                    <div style={{
                      position: "absolute", inset: 0, width: `${before}%`,
                      background: "rgba(161,161,170,0.35)",
                    }} />
                    <div style={{
                      position: "absolute", inset: 0, width: `${after}%`,
                      background: diff > 0 ? `${SENSEI_C.green}99` : diff < 0 ? `${SENSEI_C.red}99` : "rgba(161,161,170,0.35)",
                      mixBlendMode: "screen",
                    }} />
                  </div>
                  <span style={{
                    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
                    color: diff > 0 ? SENSEI_C.green : diff < 0 ? SENSEI_C.red : C.muted,
                    fontWeight: 700, textAlign: "right",
                  }}>
                    → {after}% ({diff > 0 ? "+" : ""}{diff})
                  </span>
                </div>
              );
            })}
          </div>
          {o.rationale && (
            <div style={{
              marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.border}`,
              color: C.muted, fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.7rem", lineHeight: 1.55, fontStyle: "italic",
            }}>
              "{o.rationale}"
            </div>
          )}
          {o.history && o.history.length > 1 && (
            <div style={{
              marginTop: 8, fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.58rem", color: C.muted, letterSpacing: "0.08em",
            }}>
              STORICO · {o.history.length} apply
              {o.first_applied_at && ` · dal ${new Date(o.first_applied_at).toLocaleDateString()}`}
              {o.last_reinforced_at && ` · ultimo REINFORCE ${new Date(o.last_reinforced_at).toLocaleDateString()}`}
            </div>
          )}
          <div style={{
            marginTop: 8, fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.55rem", color: C.muted, letterSpacing: "0.08em",
          }}>
            Scade {formatDate(o.expires_at)}
            {o.suggestion_only && " · ⓘ SOLO SUGGERIMENTO (non agisce sulle predizioni)"}
          </div>
        </div>
      )}
    </div>
  );
}




function SenseiConfirmDialog({ title, body, confirmLabel, confirmColor, onConfirm, onCancel, icon }) {
  const C = { surface: "var(--bc-panel, #09090B)", text: "var(--bc-ink, #FFFFFF)",
    muted: "var(--bc-sub, #71717A)", border: "var(--bc-border, rgba(255,255,255,0.07))" };
  const iconEl = icon === "danger"
    ? <AlertTriangle size={16} style={{ color: confirmColor }} />
    : <Sparkles size={16} style={{ color: confirmColor }} />;

  return (
    <div data-testid="sensei-confirm-dialog"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
      onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface, border: `1px solid ${confirmColor}55`,
          maxWidth: 460, width: "100%", padding: "1.25rem",
          boxShadow: `0 0 28px ${confirmColor}33`,
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.85rem" }}>
          {iconEl}
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: "1rem", letterSpacing: "0.1em", textTransform: "uppercase", color: C.text }}>
            {title}
          </span>
        </div>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: C.text, lineHeight: 1.6, marginBottom: "1rem" }}>
          {body}
        </div>
        <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
          <button data-testid="sensei-confirm-cancel" onClick={onCancel}
            style={{
              padding: "0.55rem 1.1rem", background: "transparent", color: C.muted,
              border: `1px solid ${C.border}`, fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 700, fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase",
              cursor: "pointer",
            }}>
            Annulla
          </button>
          <button data-testid="sensei-confirm-ok" onClick={onConfirm}
            style={{
              padding: "0.55rem 1.1rem", background: confirmColor, color: "#0A0A0A",
              border: `1px solid ${confirmColor}`, fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 800, fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase",
              cursor: "pointer", boxShadow: `0 0 14px ${confirmColor}66`,
            }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── BRAIN LAB PANEL ──────────────────────────────────────────────────────
const BL_C = {
  cyan: "#22D3EE",
  amber: "#F59E0B",
  violet: "#A78BFA",
  green: "#22FFA3",
  red: "#EF4444",
};

function BrainLabPanel({ token, onToast }) {
  const C2 = {
    surface: "var(--bc-panel, #09090B)",
    highlight: "var(--bc-panel-elev, #141416)",
    border: "var(--bc-border, rgba(255,255,255,0.07))",
    text: "var(--bc-ink, #FFFFFF)",
    muted: "var(--bc-sub, #71717A)",
  };
  const [open, setOpen] = useState(true);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [candidates, setCandidates] = useState([]); // unfiltered
  const [presets, setPresets] = useState([]);
  const [bucket, setBucket] = useState("low_odds"); // tab UI
  const [activatingKey, setActivatingKey] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [maxThreshold, setMaxThreshold] = useState(""); // "" = nessun limite | 40|50|55|60|65|70
  const [backtest, setBacktest] = useState(null); // candidate currently in backtest modal
  const [addingFor, setAddingFor] = useState(null); // candidate to add to filters
  const [addName, setAddName] = useState("");

  const loadStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/brain-lab/job-status?admin_token=${token}`);
      setJob(res.data?.job || null);
    } catch { /* silent */ }
  }, [token]);

  const loadCandidates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/brain-lab/candidates?admin_token=${token}&limit=200`);
      setCandidates(res.data?.candidates || []);
    } catch { /* silent */ }
  }, [token]);

  const loadPresets = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/brain-lab/presets?admin_token=${token}`);
      setPresets(res.data?.presets || []);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    loadStatus();
    loadCandidates();
    loadPresets();
  }, [open, loadStatus, loadCandidates, loadPresets]);

  // Poll while job running
  useEffect(() => {
    if (!open) return;
    const status = job?.status;
    if (status !== "pending" && status !== "loading" && status !== "computing") return;
    const id = setInterval(async () => {
      await loadStatus();
    }, 2500);
    return () => clearInterval(id);
  }, [open, job?.status, loadStatus]);

  // When job becomes "done", refresh candidates once
  useEffect(() => {
    if (job?.status === "done") {
      loadCandidates();
    }
  }, [job?.status, loadCandidates]);

  const handleDiscover = async (mode) => {
    if (running) return;
    setRunning(true);
    try {
      const payload = { admin_token: token, mode };
      if (maxThreshold) payload.max_threshold = parseInt(maxThreshold, 10);
      const res = await axios.post(`${API}/admin/brain-lab/discover`, payload);
      setJob({ job_id: res.data?.job_id, status: "pending", progress: 0, mode, max_threshold: payload.max_threshold });
      onToast?.(`Discovery avviata · ${mode}${payload.max_threshold ? ` · max ≥${payload.max_threshold}%` : ""}`);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Discovery fallita", "error");
    } finally {
      setRunning(false);
    }
  };

  const handleActivate = async (cand) => {
    const key = cand.keys.join("|") + ":" + cand.threshold + ":" + cand.market;
    setActivatingKey(key);
    try {
      await axios.post(`${API}/admin/brain-lab/activate`, {
        admin_token: token,
        candidate_key: cand.keys,
        threshold: cand.threshold,
        market: cand.market,
      });
      await loadPresets();
      onToast?.("Preset attivato");
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Attivazione fallita", "error");
    } finally {
      setActivatingKey(null);
    }
  };

  const handleDeactivate = async (presetId) => {
    setDeactivatingId(presetId);
    try {
      await axios.post(`${API}/admin/brain-lab/deactivate`, {
        admin_token: token,
        preset_id: presetId,
      });
      await loadPresets();
      onToast?.("Preset disattivato");
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Disattivazione fallita", "error");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleAddToFilters = () => {
    if (!addingFor) return;
    const trimmed = (addName || "").trim().slice(0, 32);
    if (!trimmed) return;
    let arr = [];
    try {
      arr = JSON.parse(localStorage.getItem("bc_saved_presets") || "[]");
    } catch { /* keep [] */ }
    arr = arr.filter((x) => x.name !== trimmed);
    arr.push({
      name: trimmed,
      cross: addingFor.keys,
      minp: addingFor.threshold,
      pred: "all",
      vb: false,
      source: "brainlab",
      market_hint: addingFor.market,
      hit_rate: addingFor.hit_rate,
    });
    localStorage.setItem("bc_saved_presets", JSON.stringify(arr));
    setAddingFor(null);
    setAddName("");
    onToast?.(`Filtro "${trimmed}" salvato`);
  };

  const filteredCandidates = candidates.filter((c) => c.mode_bucket === bucket);
  const activePresetIds = new Set(presets.filter((p) => p.active).map((p) => {
    return (p.keys || []).join("|") + ":" + p.threshold + ":" + p.market;
  }));
  const status = job?.status;
  const inProgress = status === "pending" || status === "loading" || status === "computing";

  return (
    <div
      data-testid="brain-lab-panel"
      style={{
        background: C2.surface,
        border: `1px solid ${BL_C.cyan}33`,
        padding: "1.25rem",
        marginBottom: "1.5rem",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 0 18px ${BL_C.cyan}18`,
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, height: "2px", width: "100%",
        background: `linear-gradient(90deg, transparent, ${BL_C.cyan}, ${BL_C.violet}, transparent)` }} />

      {/* Header */}
      <button
        data-testid="brain-lab-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "0.5rem",
          background: "none", border: "none", padding: 0, cursor: "pointer",
          marginBottom: open ? "1rem" : 0,
        }}
      >
        <FlaskConical size={14} style={{ color: BL_C.cyan, filter: `drop-shadow(0 0 6px ${BL_C.cyan})` }} />
        <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C2.text }}>
          Brain Lab · Cross-Filter Discovery
        </span>
        <span style={{ marginLeft: "0.5rem", padding: "0.15rem 0.5rem", background: `${BL_C.violet}14`, border: `1px solid ${BL_C.violet}40`, color: BL_C.violet, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem", letterSpacing: "0.1em" }}>
          PATTERN ENGINE · DUAL-MODE
        </span>
        <span style={{ marginLeft: "auto", color: C2.muted, fontSize: "0.7rem", fontFamily: "'JetBrains Mono',monospace" }}>
          {open ? "▼" : "▶"}
        </span>
      </button>

      {open && (
        <>
          {/* Discovery controls */}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem", alignItems: "center" }}>
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.66rem", color: C2.muted, letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}>
              Soglia ≥% max:
            </span>
            {["", "40", "50", "55", "60", "65", "70"].map((t) => {
              const active = maxThreshold === t;
              return (
                <button
                  key={t || "any"}
                  data-testid={`brain-lab-maxthr-${t || "any"}`}
                  onClick={() => setMaxThreshold(t)}
                  disabled={running || inProgress}
                  style={{
                    padding: "0.3rem 0.55rem",
                    background: active ? `${BL_C.violet}22` : "transparent",
                    color: active ? BL_C.violet : C2.muted,
                    border: `1px solid ${active ? BL_C.violet + "88" : C2.border}`,
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.66rem",
                    letterSpacing: "0.05em",
                    cursor: "pointer",
                    filter: active ? `drop-shadow(0 0 4px ${BL_C.violet})` : "none",
                  }}
                >
                  {t === "" ? "ANY" : `≤${t}`}
                </button>
              );
            })}
            <span style={{
              fontSize: "0.62rem", color: C2.muted, fontFamily: "'DM Sans',sans-serif",
              fontStyle: "italic", marginLeft: "0.25rem",
            }}>
              Più bassa = pattern più "larghi", più partite filtrate
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <button
              data-testid="brain-lab-discover-low"
              disabled={running || inProgress}
              onClick={() => handleDiscover("low_odds")}
              style={discoverBtn(BL_C.cyan, running || inProgress)}
            >
              {inProgress && job?.mode === "low_odds" ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              Discovery Low-Odds
            </button>
            <button
              data-testid="brain-lab-discover-high"
              disabled={running || inProgress}
              onClick={() => handleDiscover("high_odds")}
              style={discoverBtn(BL_C.amber, running || inProgress)}
            >
              {inProgress && job?.mode === "high_odds" ? <Loader2 size={11} className="animate-spin" /> : <TrendingUp size={11} />}
              Discovery High-Odds
            </button>
            <button
              data-testid="brain-lab-discover-both"
              disabled={running || inProgress}
              onClick={() => handleDiscover("both")}
              style={discoverBtn(BL_C.violet, running || inProgress)}
            >
              {inProgress && job?.mode === "both" ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
              Discovery Both
            </button>
            <button
              data-testid="brain-lab-refresh"
              onClick={() => { loadCandidates(); loadPresets(); loadStatus(); }}
              style={{
                padding: "0.45rem 0.85rem", background: "transparent",
                color: C2.muted, border: `1px solid ${C2.border}`,
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase",
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.35rem",
              }}
            >
              <RefreshCw size={11} /> Refresh
            </button>
          </div>

          {/* Job status */}
          {job && (
            <div style={{
              padding: "0.6rem 0.85rem", marginBottom: "1rem",
              background: C2.highlight, border: `1px solid ${C2.border}`,
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", color: C2.muted,
            }}>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: 6 }}>
                <span>JOB <span style={{ color: BL_C.cyan }}>{job.job_id || "—"}</span></span>
                <span>STATUS <span style={{ color: status === "done" ? BL_C.green : status === "error" ? BL_C.red : BL_C.amber }}>{status?.toUpperCase()}</span></span>
                <span>MODE <span style={{ color: C2.text }}>{(job.mode || "both").toUpperCase()}</span></span>
                {job.max_threshold && <span>MAX≥% <span style={{ color: BL_C.violet }}>{job.max_threshold}</span></span>}
                <span>PROGRESS <span style={{ color: C2.text }}>{job.progress || 0}%</span></span>
                {job.matches_loaded != null && <span>MATCH <span style={{ color: C2.text }}>{job.matches_loaded}</span></span>}
                {job.candidates_so_far != null && <span>CAND <span style={{ color: C2.text }}>{job.candidates_so_far}</span></span>}
                {job.candidates_total != null && <span>TOTAL <span style={{ color: BL_C.green }}>{job.candidates_total}</span></span>}
              </div>
              <div style={{ height: 4, background: C2.border, position: "relative", overflow: "hidden" }}>
                <div style={{
                  position: "absolute", inset: 0,
                  width: `${Math.max(0, Math.min(100, job.progress || 0))}%`,
                  background: `linear-gradient(90deg, ${BL_C.cyan}, ${BL_C.violet})`,
                  boxShadow: `0 0 8px ${BL_C.cyan}`,
                  transition: "width 0.4s ease",
                }} />
              </div>
              {job.error && (
                <div style={{ marginTop: 6, color: BL_C.red }}>ERR · {job.error}</div>
              )}
            </div>
          )}

          {/* Tabs bucket */}
          <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.75rem", borderBottom: `1px solid ${C2.border}` }}>
            {[
              { key: "low_odds", label: "Low-Odds (≥88% hit)", color: BL_C.cyan },
              { key: "high_odds", label: "High-Odds (≥75% hit · quote più alte)", color: BL_C.amber },
            ].map((t) => {
              const active = bucket === t.key;
              const count = candidates.filter((c) => c.mode_bucket === t.key).length;
              return (
                <button
                  key={t.key}
                  data-testid={`brain-lab-tab-${t.key}`}
                  onClick={() => setBucket(t.key)}
                  style={{
                    padding: "0.5rem 0.85rem",
                    background: "none",
                    border: "none",
                    borderBottom: active ? `2px solid ${t.color}` : "2px solid transparent",
                    color: active ? t.color : C2.muted,
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700, fontSize: "0.75rem",
                    letterSpacing: "0.12em", textTransform: "uppercase",
                    cursor: "pointer",
                    filter: active ? `drop-shadow(0 0 4px ${t.color})` : "none",
                  }}
                >
                  {t.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>· {count}</span>
                </button>
              );
            })}
          </div>

          {/* Candidates table */}
          {filteredCandidates.length === 0 ? (
            <div style={{
              padding: "1.5rem", textAlign: "center",
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: C2.muted,
              border: `1px dashed ${C2.border}`,
            }}>
              Nessun candidato per questo bucket. Avvia una nuova Discovery.
            </div>
          ) : (
            <div style={{ overflowX: "auto", marginBottom: "1.25rem" }}>
              <table data-testid="brain-lab-candidates-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C2.border}` }}>
                    {["Filtri (∩)", "≥%", "Mercato", "Hit %", "Baseline %", "Edge", "N", "Quota est.", "Azione"].map((h) => (
                      <th key={h} style={{
                        textAlign: "left", padding: "0.5rem 0.5rem",
                        color: C2.muted, fontFamily: "'Barlow Condensed',sans-serif",
                        fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
                        fontSize: "0.66rem",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.slice(0, 60).map((c, idx) => {
                    const key = c.keys.join("|") + ":" + c.threshold + ":" + c.market;
                    const isActive = activePresetIds.has(key);
                    const isActivating = activatingKey === key;
                    const edgeColor = c.edge_pp >= 20 ? BL_C.green : c.edge_pp >= 12 ? BL_C.cyan : BL_C.amber;
                    return (
                      <tr key={key + ":" + idx} style={{ borderBottom: `1px solid ${C2.border}` }}>
                        <td style={{ padding: "0.55rem 0.5rem" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {c.label_keys.map((lk, i) => (
                              <span key={i} style={{
                                padding: "0.1rem 0.4rem",
                                background: `${BL_C.cyan}14`,
                                border: `1px solid ${BL_C.cyan}40`,
                                color: BL_C.cyan,
                                fontFamily: "'JetBrains Mono',monospace",
                                fontSize: "0.62rem",
                              }}>{lk}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: C2.text, fontFamily: "'JetBrains Mono',monospace" }}>
                          {c.threshold}
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: BL_C.violet, fontWeight: 600 }}>
                          {c.label_market}
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: BL_C.green, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                          {c.hit_rate}
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: C2.muted, fontFamily: "'JetBrains Mono',monospace" }}>
                          {c.baseline}
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: edgeColor, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                          +{c.edge_pp}pp
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: C2.muted, fontFamily: "'JetBrains Mono',monospace" }}>
                          {c.n}
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem", color: C2.text, fontFamily: "'JetBrains Mono',monospace" }}>
                          {c.quote_est ? `~${c.quote_est}` : "—"}
                        </td>
                        <td style={{ padding: "0.55rem 0.5rem" }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <button
                              data-testid={`brain-lab-backtest-${idx}`}
                              onClick={() => setBacktest(c)}
                              title="Backtest 30gg"
                              style={{
                                padding: "0.22rem 0.5rem",
                                background: `${BL_C.cyan}1A`,
                                color: BL_C.cyan,
                                border: `1px solid ${BL_C.cyan}66`,
                                fontFamily: "'Barlow Condensed',sans-serif",
                                fontWeight: 700, fontSize: "0.6rem",
                                letterSpacing: "0.1em", textTransform: "uppercase",
                                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3,
                              }}
                            >
                              <Beaker size={9} /> Backtest
                            </button>
                            <button
                              data-testid={`brain-lab-add-${idx}`}
                              onClick={() => {
                                setAddingFor(c);
                                setAddName((c.label_keys || []).slice(0, 3).join(" + ").slice(0, 26));
                              }}
                              title="Aggiungi ai filtri salvati"
                              style={{
                                padding: "0.22rem 0.5rem",
                                background: `${BL_C.green}1A`,
                                color: BL_C.green,
                                border: `1px solid ${BL_C.green}66`,
                                fontFamily: "'Barlow Condensed',sans-serif",
                                fontWeight: 700, fontSize: "0.6rem",
                                letterSpacing: "0.1em", textTransform: "uppercase",
                                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3,
                              }}
                            >
                              <Plus size={9} /> Aggiungi
                            </button>
                            {isActive ? (
                              <span style={{
                                padding: "0.2rem 0.45rem",
                                background: `${BL_C.green}1A`,
                                border: `1px solid ${BL_C.green}55`,
                                color: BL_C.green,
                                fontFamily: "'Barlow Condensed',sans-serif",
                                fontWeight: 700, fontSize: "0.6rem",
                                letterSpacing: "0.1em", textTransform: "uppercase",
                              }}>
                                ✓ Attivo
                              </span>
                            ) : (
                              <button
                                data-testid={`brain-lab-activate-${idx}`}
                                disabled={isActivating}
                                onClick={() => handleActivate(c)}
                                style={{
                                  padding: "0.22rem 0.5rem",
                                  background: `${BL_C.violet}1A`,
                                  color: BL_C.violet,
                                  border: `1px solid ${BL_C.violet}66`,
                                  fontFamily: "'Barlow Condensed',sans-serif",
                                  fontWeight: 700, fontSize: "0.6rem",
                                  letterSpacing: "0.1em", textTransform: "uppercase",
                                  cursor: isActivating ? "wait" : "pointer",
                                  display: "inline-flex", alignItems: "center", gap: 3,
                                }}
                              >
                                {isActivating ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                                Attiva
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Active presets */}
          <div style={{
            borderTop: `1px solid ${C2.border}`,
            paddingTop: "0.85rem",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.6rem",
            }}>
              <ShieldCheck size={12} style={{ color: BL_C.green }} />
              <span style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.72rem", letterSpacing: "0.15em", textTransform: "uppercase",
                color: C2.text,
              }}>
                Preset Cristallo Attivi
              </span>
              <span style={{ marginLeft: "auto", color: C2.muted, fontSize: "0.66rem", fontFamily: "'JetBrains Mono',monospace" }}>
                {presets.filter((p) => p.active).length} attivi · {presets.length} totali
              </span>
            </div>
            {presets.length === 0 ? (
              <div style={{
                padding: "0.85rem", textAlign: "center",
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.74rem", color: C2.muted,
                border: `1px dashed ${C2.border}`,
              }}>
                Nessun preset attivo. Attiva un candidato dalla tabella sopra per renderlo un preset Cristallo.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {presets.map((p) => (
                  <div key={p._id || p.preset_id} data-testid={`brain-lab-preset-${p._id || p.preset_id}`}
                    style={{
                      padding: "0.6rem 0.85rem",
                      background: C2.highlight,
                      border: `1px solid ${p.active ? BL_C.green + "44" : C2.border}`,
                      display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
                      opacity: p.active ? 1 : 0.55,
                    }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1, minWidth: 200 }}>
                      {(p.label_keys || []).map((lk, i) => (
                        <span key={i} style={{
                          padding: "0.1rem 0.4rem",
                          background: `${BL_C.cyan}14`,
                          border: `1px solid ${BL_C.cyan}40`,
                          color: BL_C.cyan,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: "0.62rem",
                        }}>{lk}</span>
                      ))}
                      <span style={{
                        padding: "0.1rem 0.4rem",
                        background: `${BL_C.violet}14`,
                        border: `1px solid ${BL_C.violet}40`,
                        color: BL_C.violet,
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.62rem",
                      }}>→ {p.label_market}</span>
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem", color: C2.muted, display: "flex", gap: 10 }}>
                      <span>HIT <span style={{ color: BL_C.green }}>{p.hit_rate}%</span></span>
                      <span>EDGE <span style={{ color: BL_C.cyan }}>+{p.edge_pp}pp</span></span>
                      <span>N <span style={{ color: C2.text }}>{p.n}</span></span>
                    </div>
                    {p.active ? (
                      <button
                        data-testid={`brain-lab-deactivate-${p._id || p.preset_id}`}
                        disabled={deactivatingId === (p._id || p.preset_id)}
                        onClick={() => handleDeactivate(p._id || p.preset_id)}
                        style={{
                          padding: "0.25rem 0.6rem",
                          background: `${BL_C.red}1A`,
                          color: BL_C.red,
                          border: `1px solid ${BL_C.red}66`,
                          fontFamily: "'Barlow Condensed',sans-serif",
                          fontWeight: 700, fontSize: "0.64rem",
                          letterSpacing: "0.1em", textTransform: "uppercase",
                          cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                        {deactivatingId === (p._id || p.preset_id)
                          ? <Loader2 size={10} className="animate-spin" />
                          : <X size={10} />}
                        Disattiva
                      </button>
                    ) : (
                      <span style={{ color: C2.muted, fontSize: "0.66rem", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                        Disattivato
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Backtest modal */}
      {backtest && (
        <BrainLabBacktestModal
          candidate={backtest}
          days={30}
          onClose={() => setBacktest(null)}
        />
      )}

      {/* Add to saved filters dialog */}
      {addingFor && (
        <div
          onClick={() => setAddingFor(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1200,
            background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
          }}
        >
          <div onClick={(e) => e.stopPropagation()}
            style={{
              background: C2.surface, border: `1px solid ${BL_C.green}55`,
              boxShadow: `0 0 24px ${BL_C.green}33`,
              padding: "1.2rem 1.4rem", maxWidth: 460, width: "100%",
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.9rem" }}>
              <Plus size={13} style={{ color: BL_C.green }} />
              <span style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.85rem", letterSpacing: "0.15em",
                textTransform: "uppercase", color: C2.text,
              }}>
                Aggiungi ai filtri
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: "0.85rem" }}>
              {(addingFor.label_keys || []).map((lk, i) => (
                <span key={i} style={{
                  padding: "0.12rem 0.4rem",
                  background: `${BL_C.cyan}14`,
                  border: `1px solid ${BL_C.cyan}40`,
                  color: BL_C.cyan,
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
                }}>{lk}</span>
              ))}
              <span style={{ color: C2.muted, fontSize: "0.72rem", marginLeft: 4 }}>
                ≥{addingFor.threshold}%
              </span>
            </div>
            <label style={{
              display: "block",
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.65rem", letterSpacing: "0.15em",
              textTransform: "uppercase", color: C2.muted, marginBottom: 4,
            }}>
              Nome del filtro (max 32)
            </label>
            <input
              data-testid="brain-lab-add-name"
              autoFocus
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddToFilters()}
              maxLength={32}
              placeholder="Es. X2 + MgCasa + Over1.5"
              style={{
                width: "100%", background: C2.highlight,
                border: `1px solid ${C2.border}`, color: C2.text,
                padding: "0.6rem 0.7rem",
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
                outline: "none", marginBottom: "1rem",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button
                data-testid="brain-lab-add-cancel"
                onClick={() => setAddingFor(null)}
                style={{
                  padding: "0.5rem 1rem", background: "transparent",
                  color: C2.muted, border: `1px solid ${C2.border}`,
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  fontSize: "0.72rem", letterSpacing: "0.12em",
                  textTransform: "uppercase", cursor: "pointer",
                }}>
                Annulla
              </button>
              <button
                data-testid="brain-lab-add-confirm"
                onClick={handleAddToFilters}
                disabled={!addName.trim()}
                style={{
                  padding: "0.5rem 1rem", background: BL_C.green,
                  color: "#0A0A0A", border: `1px solid ${BL_C.green}`,
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                  fontSize: "0.72rem", letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  cursor: addName.trim() ? "pointer" : "not-allowed",
                  opacity: addName.trim() ? 1 : 0.5,
                  boxShadow: `0 0 14px ${BL_C.green}55`,
                }}>
                Salva filtro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function discoverBtn(color, disabled) {
  return {
    padding: "0.5rem 0.95rem",
    background: `${color}1A`,
    color,
    border: `1px solid ${color}66`,
    fontFamily: "'Barlow Condensed',sans-serif",
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.6 : 1,
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    boxShadow: `0 0 10px ${color}33`,
  };
}


export default function AdminPage() {
  const [adminSession, setAdminSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newDuration, setNewDuration] = useState("permanent");
  const [toast, setToast] = useState(null);
  const [cleaningStats, setCleaningStats] = useState(false);
  const [cleanStatsResult, setCleanStatsResult] = useState(null);
  const [brainState, setBrainState] = useState(null);
  const [recalibrating, setRecalibrating] = useState(false);
  const [lastRecal, setLastRecal] = useState(null); // { before, after, version, runAt }

  // Sensei Recalibration (AI-powered, Claude Sonnet 4.5)
  const [senseiRunning, setSenseiRunning] = useState(false);
  const [senseiLastRun, setSenseiLastRun] = useState(null);
  const [senseiOverrides, setSenseiOverrides] = useState([]);
  const [applyingActionId, setApplyingActionId] = useState(null);
  const [confirmApply, setConfirmApply] = useState(null); // { action, runId, preview }
  const [confirmDisableAll, setConfirmDisableAll] = useState(false);
  const [senseiLoading, setSenseiLoading] = useState(false);
  const [senseiJobId, setSenseiJobId] = useState(null);
  const [senseiPhase, setSenseiPhase] = useState(""); // user-friendly phase label
  const [reverifying, setReverifying] = useState(false);
  const [backtesting, setBacktesting] = useState(false);
  const [backtestResult, setBacktestResult] = useState(null);
  const [backtestExpanded, setBacktestExpanded] = useState(true);
  const [senseiPanelOpen, setSenseiPanelOpen] = useState(false); // collapsed by default

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const verifySession = useCallback(async (session) => {
    if (!session?.token) { setChecking(false); return; }
    try {
      const res = await axios.post(`${API}/auth/check`, { admin_token: session.token });
      if (res.data.valid) { setAdminSession(session); } else { localStorage.removeItem("bc_admin_session"); }
    } catch { setAdminSession(session); } // trust on network error
    setChecking(false);
  }, []);

  useEffect(() => {
    const stored = getAdminSession();
    if (stored) { verifySession(stored); } else { setChecking(false); }
  }, [verifySession]);

  const loadCodes = useCallback(async (session) => {
    const tok = session?.token;
    if (!tok) return;
    setLoadingCodes(true);
    try {
      const res = await axios.get(`${API}/admin/codes?admin_token=${tok}`);
      setCodes(res.data.codes || []);
    } catch (err) {
      // 403 = token scaduto/non valido → forza logout e torna al login
      if (err?.response?.status === 403) {
        localStorage.removeItem(ADMIN_SESSION_KEY);
        localStorage.removeItem("bc_access_session");
        setAdminSession(null);
        setCodes([]);
      } else {
        showToast("Errore nel caricamento dei codici. Riprova.", "error");
      }
    }
    finally { setLoadingCodes(false); }
  }, []);

  useEffect(() => {
    if (adminSession) loadCodes(adminSession);
  }, [adminSession, loadCodes]);

  const loadBrainState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/learning/state`);
      setBrainState(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (adminSession) loadBrainState();
  }, [adminSession, loadBrainState]);

  const handleRecalibrate = async () => {
    if (recalibrating) return;
    setRecalibrating(true);
    const before = brainState?.metrics || {};
    try {
      const res = await axios.post(`${API}/learning/admin-recalibrate`, {
        admin_token: adminSession.token,
      });
      const after = res.data?.metrics || {};
      setBrainState(res.data);
      setLastRecal({
        before: before.logloss_after,
        after: after.logloss_after,
        version: res.data?.version,
        runAt: new Date(),
      });
      showToast(`Cervello ricalibrato → v${res.data?.version}`);
    } catch (err) {
      showToast(err?.response?.data?.detail || "Ricalibrazione fallita", "error");
    } finally {
      setRecalibrating(false);
    }
  };

  // ── Sensei Recalibration (Claude Sonnet 4.5 + Hanzo Verifier) ──
  const loadSenseiData = useCallback(async (session) => {
    const tok = session?.token;
    if (!tok) return;
    setSenseiLoading(true);
    try {
      const [runsRes, ovsRes] = await Promise.all([
        axios.get(`${API}/admin/sensei-recalibration/runs?admin_token=${tok}&limit=1`),
        axios.get(`${API}/admin/sensei-recalibration/overrides?admin_token=${tok}`),
      ]);
      if (runsRes.data?.runs?.length) setSenseiLastRun(runsRes.data.runs[0]);
      setSenseiOverrides(ovsRes.data?.overrides || []);
    } catch {
      /* silent: la sezione resta vuota fino al primo run */
    } finally {
      setSenseiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminSession) loadSenseiData(adminSession);
  }, [adminSession, loadSenseiData]);

  const handleSenseiRun = async () => {
    if (senseiRunning) return;
    setSenseiRunning(true);
    setSenseiPhase("Avvio analisi...");
    try {
      const res = await axios.post(`${API}/admin/sensei-recalibration/run`, {
        admin_token: adminSession.token,
      });
      const jobId = res.data?.job_id;
      if (!jobId) {
        showToast("Risposta inattesa dal server", "error");
        setSenseiRunning(false);
        return;
      }
      setSenseiJobId(jobId);
      setSenseiPhase("Sensei sta analizzando lo storico…");

      // Poll job-status every 5s (max 240 attempts = 20 min budget,
      // analisi DEEP con 16K tokens può richiedere 3-8 minuti)
      let attempts = 0;
      const maxAttempts = 240;
      const poll = async () => {
        attempts += 1;
        try {
          const s = await axios.get(`${API}/admin/sensei-recalibration/job-status?admin_token=${adminSession.token}&job_id=${jobId}`);
          const status = s.data?.status;
          if (status === "completed") {
            await loadSenseiData(adminSession);
            showToast(`Sensei completato`);
            setSenseiRunning(false);
            setSenseiJobId(null);
            setSenseiPhase("");
            return;
          }
          if (status === "failed") {
            const errMsg = s.data?.error || "Run fallito";
            showToast(errMsg, "error");
            await loadSenseiData(adminSession);
            setSenseiRunning(false);
            setSenseiJobId(null);
            setSenseiPhase("");
            return;
          }
          if (status === "unknown") {
            showToast("Job interrotto o non trovato. Riprova.", "error");
            await loadSenseiData(adminSession);
            setSenseiRunning(false);
            setSenseiJobId(null);
            setSenseiPhase("");
            return;
          }
          // running → continua il polling
          if (attempts < maxAttempts) {
            setTimeout(poll, 5000);
          } else {
            showToast("Timeout: il job sta impiegando troppo. Controlla i log.", "error");
            setSenseiRunning(false);
            setSenseiJobId(null);
            setSenseiPhase("");
          }
        } catch (err) {
          showToast(err?.response?.data?.detail || "Errore polling", "error");
          setSenseiRunning(false);
          setSenseiJobId(null);
          setSenseiPhase("");
        }
      };
      setTimeout(poll, 4000); // primo poll dopo 4s
    } catch (err) {
      showToast(err?.response?.data?.detail || "Errore avvio Sensei", "error");
      setSenseiRunning(false);
      setSenseiPhase("");
    }
  };

  const handleSenseiBacktest = async () => {
    const runId = senseiLastRun?.run_id || senseiLastRun?._id;
    if (!runId || backtesting) return;
    setBacktesting(true);
    setBacktestResult(null);
    try {
      const res = await axios.post(`${API}/admin/sensei-recalibration/backtest`, {
        admin_token: adminSession.token,
        run_id: runId,
      });
      if (res.data?.error) {
        showToast(res.data.error, "error");
      } else {
        setBacktestResult(res.data);
        setBacktestExpanded(true);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || "Errore backtest", "error");
    } finally {
      setBacktesting(false);
    }
  };

  const handleSenseiReverify = async () => {
    const runId = senseiLastRun?.run_id || senseiLastRun?._id;
    if (!runId || reverifying) return;
    setReverifying(true);
    try {
      const res = await axios.post(`${API}/admin/sensei-recalibration/reverify`, {
        admin_token: adminSession.token,
        run_id: runId,
      });
      if (res.data?.error) {
        showToast(res.data.error, "error");
      } else {
        setSenseiLastRun(res.data);
        showToast("Verifica completata");
        loadSenseiData(adminSession);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || "Errore re-verifica", "error");
    } finally {
      setReverifying(false);
    }
  };

  const handleSenseiApplyConfirmed = async () => {
    const c = confirmApply;
    if (!c) return;
    setApplyingActionId(c.action.id);
    setConfirmApply(null);
    try {
      const res = await axios.post(`${API}/admin/sensei-recalibration/apply`, {
        admin_token: adminSession.token,
        run_id: c.runId,
        action_id: c.action.id,
      });
      if (res.data?.error) {
        showToast(`Apply rifiutato: ${res.data.error}`, "error");
      } else {
        const dec = res.data?.decision || "NEW";
        const legacy = res.data?.deactivated_legacy?.length || 0;
        const reinforce = res.data?.reinforce_count || 0;
        let msg = `Override applicato · ${dec}`;
        if (dec === "REINFORCE" && reinforce > 0) msg += ` (×${reinforce + 1})`;
        if (legacy > 0) msg += ` · ${legacy} legacy disattivati`;
        showToast(msg);
        // Aggiorna localmente lo stato applied dell'azione + ricarica overrides
        setSenseiLastRun((prev) => prev ? {
          ...prev,
          recommended_actions: (prev.recommended_actions || []).map((a) =>
            a.id === c.action.id ? { ...a, applied: true, applied_at: res.data.applied_at, decision: dec } : a
          ),
        } : prev);
        loadSenseiData(adminSession);
      }
    } catch (err) {
      // Mostra un messaggio molto più diagnostico: status code + detail backend +
      // primi N caratteri del body. Senza questo, "Errore applicazione" non
      // dice nulla all'admin (vedi screenshot toast vuoto in produzione).
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === "string" ? err.response.data.slice(0, 120) : null) ||
        err?.message;
      console.error("Sensei apply error:", err?.response || err);
      showToast(`Errore apply${status ? ` ${status}` : ""}: ${detail || "errore sconosciuto"}`, "error");
    } finally {
      setApplyingActionId(null);
    }
  };

  const handleSenseiDisableAll = async () => {
    setConfirmDisableAll(false);
    try {
      const res = await axios.post(`${API}/admin/sensei-recalibration/disable-all`, {
        admin_token: adminSession.token,
      });
      showToast(`Disattivati ${res.data?.disabled ?? 0} override`);
      loadSenseiData(adminSession);
    } catch {
      showToast("Errore disattivazione override", "error");
    }
  };

  const handleLogin = (session) => {
    setAdminSession(session);
    // Also grant access to the main app with admin session
    const appSession = { type: "admin", token: session.token };
    localStorage.setItem("bc_access_session", JSON.stringify(appSession));
  };

  const handleCleanStats = async () => {
    if (!window.confirm(
      "Sei sicuro di voler rimuovere le statistiche delle leghe non in whitelist?\n\nQuesta operazione è irreversibile."
    )) return;
    setCleaningStats(true);
    setCleanStatsResult(null);
    try {
      const res = await axios.post(`${API}/admin/clean-stats`, { admin_token: adminSession.token });
      setCleanStatsResult(res.data);
      showToast(`Pulizia completata: ${res.data.deleted} record rimossi`);
    } catch {
      showToast("Errore durante la pulizia delle statistiche", "error");
    } finally {
      setCleaningStats(false);
    }
  };

  const handleLogout = async () => {
    if (adminSession?.token) {
      try { await axios.post(`${API}/auth/admin-logout`, { admin_token: adminSession.token }); } catch {}
    }
    localStorage.removeItem("bc_admin_session");
    localStorage.removeItem("bc_access_session");
    setAdminSession(null);
    setCodes([]);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/codes/generate`, {
        admin_token: adminSession.token,
        label: newLabel.trim(),
        duration: newDuration,
      });
      setCodes((prev) => [{
        code: res.data.code, label: res.data.label,
        created_at: res.data.created_at,
        is_active: false, last_seen: null,
        duration: res.data.duration,
        expires_at: res.data.expires_at,
      }, ...prev]);
      setNewLabel("");
      setNewDuration("permanent");
      copyToClipboard(res.data.code);
      const durLabel = res.data.duration === "permanent" ? "permanente" : res.data.duration;
      showToast(`Codice (${durLabel}) generato e copiato: ${res.data.code}`);
    } catch { showToast("Errore nella generazione", "error"); }
    finally { setGenerating(false); }
  };

  const handleDelete = async (code) => {
    await axios.delete(`${API}/admin/codes/${code}?admin_token=${adminSession.token}`);
    setCodes((prev) => prev.filter((c) => c.code !== code));
    showToast("Codice eliminato");
  };

  const handleRevoke = async (code) => {
    await axios.post(`${API}/admin/codes/${code}/revoke`, { admin_token: adminSession.token });
    setCodes((prev) => prev.map((c) => c.code === code ? { ...c, is_active: false, last_seen: c.last_seen } : c));
    showToast("Dispositivo rimosso — l'utente dovrà reinserire il codice");
  };

  const handleExtend = async (code, target) => {
    try {
      const res = await axios.post(`${API}/admin/codes/${code}/extend`, {
        admin_token: adminSession.token,
        target,
      });
      setCodes((prev) => prev.map((c) => c.code === code
        ? { ...c, duration: res.data.duration, expires_at: res.data.expires_at }
        : c
      ));
      const lbl = target === "permanent" ? "permanente" : "24 ore";
      showToast(`Codice ${code} esteso a ${lbl}`);
    } catch (err) {
      showToast(err?.response?.data?.detail || "Estensione fallita", "error");
    }
  };

  if (checking) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader2 size={24} style={{ color: C.green, animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!adminSession) return <AdminLoginForm onLogin={handleLogin} />;

  const activeCount = codes.filter((c) => c.is_active).length;

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 999, padding: "0.75rem 1rem", background: toast.type === "error" ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.12)", border: `1px solid ${toast.type === "error" ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`, color: toast.type === "error" ? C.red : C.green, fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", maxWidth: 320 }}>
          {toast.msg}
        </div>
      )}

      {/* Sensei: Confirm Apply Action Dialog */}
      {confirmApply && (
        <SenseiConfirmDialog
          title="Applicare questa ricalibrazione?"
          icon="apply"
          body={
            <>
              <p style={{ marginBottom: "0.6rem" }}>
                <strong style={{ color: C.text }}>{confirmApply.action.title}</strong>
              </p>
              <p style={{ color: C.muted, fontSize: "0.78rem", marginBottom: "0.6rem" }}>
                {confirmApply.action.user_friendly_explanation}
              </p>
              <p style={{ color: "#FF6FD8", fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace" }}>
                Δ {confirmApply.action.delta_pp > 0 ? "+" : ""}{confirmApply.action.delta_pp}pp · {confirmApply.action.target_market} · {confirmApply.action.target_scope}
              </p>
              {confirmApply.preview && (
                <div data-testid="preview-apply-block" style={{
                  marginTop: "0.8rem",
                  padding: "0.7rem 0.85rem",
                  background: "var(--bc-panel-elev, #141416)",
                  border: `1px solid ${
                    confirmApply.preview.decision === "REINFORCE" ? "rgba(34,255,163,0.5)" :
                    confirmApply.preview.decision === "REPLACE" ? "rgba(167,139,250,0.5)" :
                    "rgba(157,246,255,0.5)"
                  }`,
                  borderLeftWidth: 3,
                }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
                    letterSpacing: "0.14em", textTransform: "uppercase",
                    color: confirmApply.preview.decision === "REINFORCE" ? "#22FFA3" :
                           confirmApply.preview.decision === "REPLACE" ? "#A78BFA" : "#9DF6FF",
                    marginBottom: 5,
                  }}>
                    Decisione: {confirmApply.preview.decision}
                    {confirmApply.preview.is_idempotent && " · IDEMPOTENTE (nessun cambio reale)"}
                  </div>
                  {confirmApply.preview.existing && (
                    <div style={{ color: C.muted, fontSize: "0.7rem", lineHeight: 1.55 }}>
                      Esiste già un override sullo stesso target da {confirmApply.preview.existing.first_applied_at ? new Date(confirmApply.preview.existing.first_applied_at).toLocaleDateString() : "—"}, applicato {(confirmApply.preview.existing.reinforce_count || 0) + 1} volte.
                      Valore attuale: <strong style={{ color: C.text }}>Δ{confirmApply.preview.existing.delta_pp > 0 ? "+" : ""}{confirmApply.preview.existing.delta_pp}pp</strong>.
                    </div>
                  )}
                  <div style={{ color: C.text, fontSize: "0.74rem", marginTop: 5, fontFamily: "'JetBrains Mono',monospace" }}>
                    Delta differenziale effettivo applicato: <strong style={{ color: "#FF6FD8" }}>{confirmApply.preview.differential_delta_pp_to_apply > 0 ? "+" : ""}{confirmApply.preview.differential_delta_pp_to_apply}pp</strong>
                    <span style={{ color: C.muted, fontSize: "0.65rem", marginLeft: 6 }}>(stato finale, non sommato)</span>
                  </div>
                  {confirmApply.preview.legacy_overlap_will_deactivate?.length > 0 && (
                    <div style={{ color: "#A78BFA", fontSize: "0.68rem", marginTop: 6 }}>
                      ⚠ {confirmApply.preview.legacy_overlap_will_deactivate.length} override legacy sullo stesso target verranno disattivati automaticamente.
                    </div>
                  )}
                </div>
              )}
              <p style={{ color: C.muted, fontSize: "0.72rem", marginTop: "0.6rem" }}>
                L'override sarà attivo per <strong style={{ color: C.text }}>60 giorni</strong>. Le predizioni di oggi e dei prossimi match verranno aggiornate al volo.
              </p>
            </>
          }
          confirmLabel="Applica override"
          confirmColor="#FF6FD8"
          onConfirm={handleSenseiApplyConfirmed}
          onCancel={() => setConfirmApply(null)}
        />
      )}

      {/* Sensei: Confirm Disable All Dialog */}
      {confirmDisableAll && (
        <SenseiConfirmDialog
          title="Disattivare TUTTI gli override AI?"
          icon="danger"
          body={
            <>
              <p style={{ color: C.text, marginBottom: "0.6rem" }}>
                Stai per disattivare <strong style={{ color: C.red }}>{senseiOverrides.length}</strong> override attivi. Le predizioni torneranno al comportamento del modello matematico puro (senza correzioni Sensei).
              </p>
              <p style={{ color: C.muted, fontSize: "0.72rem" }}>
                Operazione reversibile: potrai riapplicare le azioni dai run salvati.
              </p>
            </>
          }
          confirmLabel="Disattiva tutti"
          confirmColor={C.red}
          onConfirm={handleSenseiDisableAll}
          onCancel={() => setConfirmDisableAll(false)}
        />
      )}

      {/* Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 1rem", height: 56, display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Activity size={15} style={{ color: C.green }} />
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: "1.1rem", letterSpacing: "0.1em", color: C.text, textTransform: "uppercase" }}>
            BET <span style={{ color: C.green }}>CRACKER</span>
          </span>
          <Shield size={12} style={{ color: C.yellow, marginLeft: "0.25rem" }} />
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: C.muted, textTransform: "uppercase", letterSpacing: "0.15em" }}>Admin</span>

          {/* Go to app */}
          <a href="/" style={{ marginLeft: "auto", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.1em", color: C.muted, textDecoration: "none", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "0.4rem" }}
            onMouseEnter={(e) => e.currentTarget.style.color = C.text}
            onMouseLeave={(e) => e.currentTarget.style.color = C.muted}>
            <Activity size={12} /> App
          </a>
          <button data-testid="admin-logout-btn" onClick={handleLogout}
            style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.1em", color: C.muted, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.4rem", textTransform: "uppercase" }}
            onMouseEnter={(e) => e.currentTarget.style.color = C.red}
            onMouseLeave={(e) => e.currentTarget.style.color = C.muted}>
            <LogOut size={12} /> Esci
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "1.5rem 1rem 4rem" }}>
        {/* Stats strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.75rem", marginBottom: "1.5rem" }}>
          {[
            { label: "Codici Totali", value: codes.length, color: C.text },
            { label: "Attivi (in uso)", value: activeCount, color: C.green },
            { label: "Liberi", value: codes.length - activeCount, color: C.muted },
          ].map((s) => (
            <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, padding: "1rem", textAlign: "center" }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: "1.75rem", color: s.color }}>{s.value}</div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: C.muted, marginTop: "0.2rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Brain Cracker · Self-Learning */}
        <div data-testid="brain-cracker-admin-panel"
          style={{
            background: C.surface,
            border: `1px solid #22FFA333`,
            padding: "1.25rem",
            marginBottom: "1.5rem",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 0 18px rgba(34,255,163,0.08)",
          }}>
          <div style={{ position: "absolute", top: 0, left: 0, height: "2px", width: "100%",
            background: "linear-gradient(90deg, transparent, #22FFA3, transparent)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
            <Brain size={14} style={{ color: "#22FFA3", filter: "drop-shadow(0 0 6px #22FFA3)" }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
              Brain Cracker · Auto-Apprendimento
            </span>
          </div>

          {/* Mini stats inline */}
          {brainState?.applied ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.4rem", color: "#22FFA3", fontWeight: 600 }}>
                  v{brainState.version}
                </div>
                <div style={{ fontSize: "0.65rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginTop: "0.1rem" }}>
                  Versione corrente
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.4rem", color: C.text, fontWeight: 600 }}>
                  {(brainState.metrics?.n_total_samples || 0).toLocaleString("it-IT")}
                </div>
                <div style={{ fontSize: "0.65rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginTop: "0.1rem" }}>
                  Match analizzati
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.4rem", color: C.cyan, fontWeight: 600 }}>
                  {(brainState.metrics?.n_markets_calibrated || 0) + (brainState.metrics?.team_markets_count || 0)}
                </div>
                <div style={{ fontSize: "0.65rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginTop: "0.1rem" }}>
                  Mercati calibrati
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.4rem", color: C.yellow, fontWeight: 600 }}>
                  +{(brainState.metrics?.improvement_pct || 0).toFixed(1)}%
                </div>
                <div style={{ fontSize: "0.65rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginTop: "0.1rem" }}>
                  Boost accuratezza
                </div>
              </div>
            </div>
          ) : (
            <p style={{ color: C.muted, fontSize: "0.78rem", marginBottom: "1rem" }}>
              Nessuna calibrazione disponibile. Premi il pulsante per generare la prima.
            </p>
          )}

          {/* Last recal banner (only after a manual run) */}
          {lastRecal && (
            <div style={{
              padding: "0.6rem 0.85rem", marginBottom: "0.9rem",
              background: "rgba(34,255,163,0.06)", border: "1px solid rgba(34,255,163,0.25)",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", color: "#22FFA3",
            }}>
              ✓ Ricalibrazione completata · v{lastRecal.version}
              {lastRecal.before && lastRecal.after && (
                <> · LogLoss {Number(lastRecal.before).toFixed(4)} → {Number(lastRecal.after).toFixed(4)}</>
              )}
            </div>
          )}

          {/* CTA */}
          <button data-testid="recalibrate-now-btn"
            onClick={handleRecalibrate} disabled={recalibrating}
            style={{
              width: "100%", padding: "0.75rem 1rem",
              background: recalibrating ? "rgba(34,255,163,0.15)" : "#22FFA3",
              color: recalibrating ? "#22FFA3" : "#020202",
              border: "1.5px solid #22FFA3",
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.9rem",
              letterSpacing: "0.18em", textTransform: "uppercase",
              cursor: recalibrating ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.55rem",
              boxShadow: recalibrating ? "none" : "0 0 18px rgba(34,255,163,0.35)",
              transition: "all 0.2s",
            }}>
            {recalibrating
              ? <><Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} /> Ricalibrazione in corso (~10-30s)…</>
              : <><Zap size={14} /> Ricalibra ora il cervello</>}
          </button>
          <p style={{ fontSize: "0.7rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginTop: "0.6rem", lineHeight: 1.5 }}>
            Forza una nuova ricalibrazione completa (Fase 1, 1.5, 1.6, 2, 4) usando tutti i match conclusi attualmente in database.
            Il cervello si ricalibra anche automaticamente ogni domenica alle 02:00.
          </p>
        </div>

        {/* ──────────────────────────────────────────────────────── */}
        {/* SENSEI RECALIBRATION · Claude Sonnet 4.5 + Hanzo Verifier */}
        {/* ──────────────────────────────────────────────────────── */}
        <div data-testid="sensei-collapsible-wrapper" style={{ marginBottom: "1.5rem" }}>
          <button
            data-testid="sensei-collapsible-toggle"
            onClick={() => setSenseiPanelOpen((v) => !v)}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", gap: "0.5rem",
              padding: "0.9rem 1.1rem",
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderTop: `2px solid #FF6FD8`,
              color: C.text,
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 700, fontSize: "0.85rem",
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: "pointer",
              boxShadow: senseiPanelOpen ? "none" : "0 0 14px rgba(255,111,216,0.10)",
            }}
          >
            <Sword size={14} style={{ color: "#FF6FD8", filter: "drop-shadow(0 0 6px #FF6FD8)" }} />
            <span>Sensei AI · Ricalibrazione Profonda</span>
            <span style={{
              marginLeft: "0.5rem", padding: "0.15rem 0.5rem",
              background: "rgba(167,139,250,0.08)",
              border: "1px solid rgba(167,139,250,0.4)",
              color: "#A78BFA",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem", letterSpacing: "0.1em",
            }}>
              CLAUDE SONNET 4.5
            </span>
            {senseiOverrides?.length > 0 && (
              <span style={{
                padding: "0.15rem 0.5rem",
                background: "rgba(34,255,163,0.10)",
                border: "1px solid rgba(34,255,163,0.4)",
                color: "#22FFA3",
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem", letterSpacing: "0.1em",
              }}>
                {senseiOverrides.filter(o => o.applied).length} ATTIVI
              </span>
            )}
            <span style={{ marginLeft: "auto", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
              {senseiPanelOpen ? "▼" : "▶"}
            </span>
          </button>
          {senseiPanelOpen && (
            <SenseiRecalibrationPanel
              lastRun={senseiLastRun}
              overrides={senseiOverrides}
              running={senseiRunning}
              loading={senseiLoading}
              applyingActionId={applyingActionId}
              phase={senseiPhase}
              reverifying={reverifying}
              backtesting={backtesting}
              backtestResult={backtestResult}
              backtestExpanded={backtestExpanded}
              onRun={handleSenseiRun}
              onAskApply={async (action, runId) => {
                // Pre-flight: fetch preview so the admin sees the decision and what gets replaced.
                try {
                  const res = await axios.post(`${API}/admin/sensei-recalibration/preview-apply`,
                    { admin_token: adminSession.token, run_id: runId, action_id: action.id });
                  setConfirmApply({ action, runId, preview: res.data });
                } catch {
                  setConfirmApply({ action, runId, preview: null });
                }
              }}
              onAskDisableAll={() => setConfirmDisableAll(true)}
              onRefresh={() => loadSenseiData(adminSession)}
              onReverify={handleSenseiReverify}
              onDeactivateOverride={async (ov) => {
                if (!window.confirm(`Disattivare l'override "${ov.title}"?\nMercato: ${ov.target_market}\nScope: ${ov.target_scope || "global"}`)) return;
                try {
                  await axios.post(`${API}/admin/sensei-recalibration/deactivate-override`,
                    { admin_token: adminSession.token, override_id: ov._id, reason: "admin_manual" });
                  showToast(`Override "${ov.title}" disattivato`, "success");
                  loadSenseiData(adminSession);
                } catch (e) {
                  showToast(e.response?.data?.detail || "Errore disattivazione", "error");
                }
              }}
              onBacktest={handleSenseiBacktest}
              onToggleBacktest={() => setBacktestExpanded(v => !v)}
            />
          )}
        </div>

        {/* ──────────────────────────────────────────────────────── */}
        {/* BRAIN LAB · Cross-Filter Pattern Discovery               */}
        {/* ──────────────────────────────────────────────────────── */}
        <BrainLabPanel token={adminSession?.token} onToast={showToast} />

        {/* ──────────────────────────────────────────────────────── */}
        {/* AI DAILY PICKS · Claude Opus 5                            */}
        {/* ──────────────────────────────────────────────────────── */}
        <AiPicksPanel token={adminSession?.token} onToast={showToast} />

        {/* ──────────────────────────────────────────────────────── */}
        {/* MONOLITE · storico verdetti unici ed esiti                */}
        {/* ──────────────────────────────────────────────────────── */}
        <MonoliteHistoryPanel token={adminSession?.token} onToast={showToast} />

        {/* ──────────────────────────────────────────────────────── */}
        {/* USERS & PREMIUM MANAGEMENT                               */}
        {/* ──────────────────────────────────────────────────────── */}
        <UsersAdminPanel token={adminSession?.token} onToast={showToast} />


        {/* Generate new code */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, padding: "1.25rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
            <Plus size={13} style={{ color: C.green }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>Genera Nuovo Codice</span>
          </div>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <input
              data-testid="new-code-label-input"
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
              placeholder="Etichetta utente (opzionale)"
              style={{ flex: 1, background: C.highlight, border: `1px solid ${C.border}`, color: C.text, padding: "0.65rem 0.75rem", fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", outline: "none" }}
            />
            <button
              data-testid="generate-code-btn"
              onClick={handleGenerate}
              disabled={generating}
              style={{ padding: "0.65rem 1.25rem", background: C.green, color: "#020202", border: "none", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.12em", textTransform: "uppercase", cursor: generating ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {generating ? <Loader2 size={13} style={{ animation: "spin 0.8s linear infinite" }} /> : <KeyRound size={13} />}
              Genera
            </button>
          </div>
          {/* Duration selector */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem" }}>
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.7rem", color: C.muted, textTransform: "uppercase", letterSpacing: "0.15em", marginRight: "0.25rem" }}>Durata:</span>
            {[
              { value: "permanent", label: "Permanente" },
              { value: "1h",        label: "1 ora" },
              { value: "24h",       label: "24 ore" },
            ].map((opt) => {
              const active = newDuration === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`duration-${opt.value}`}
                  onClick={() => setNewDuration(opt.value)}
                  style={{
                    padding: "0.35rem 0.75rem",
                    background: active ? C.green : "transparent",
                    color: active ? "#020202" : C.muted,
                    border: `1px solid ${active ? C.green : C.border}`,
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700, fontSize: "0.72rem",
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: C.muted, marginTop: "0.6rem" }}>
            Il codice viene copiato automaticamente nella clipboard. Formato: <span style={{ color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>BCRK-XXXX-XXXX</span>
            {newDuration !== "permanent" && (
              <span style={{ color: C.yellow, marginLeft: "0.5rem" }}>
                · Countdown di {newDuration === "1h" ? "1 ora" : "24 ore"} parte al primo accesso del codice.
              </span>
            )}
          </p>
        </div>

        {/* Codes list */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.85rem 1rem", borderBottom: `1px solid ${C.border}` }}>
            <KeyRound size={13} style={{ color: C.cyan }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>Codici di Accesso</span>
            <span style={{ marginLeft: "auto", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", color: C.muted }}>{codes.length} totali</span>
            <button onClick={() => loadCodes(adminSession)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex", padding: "0.2rem" }}
              onMouseEnter={(e) => e.currentTarget.style.color = C.text} onMouseLeave={(e) => e.currentTarget.style.color = C.muted}>
              <RefreshCw size={13} className={loadingCodes ? "animate-spin" : ""} />
            </button>
          </div>

          {loadingCodes ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <Loader2 size={20} style={{ color: C.muted, animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
            </div>
          ) : codes.length === 0 ? (
            <div style={{ padding: "2.5rem", textAlign: "center" }}>
              <KeyRound size={32} style={{ color: "#27272A", margin: "0 auto 0.75rem" }} />
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: C.muted }}>Nessun codice generato. Crea il primo codice qui sopra.</p>
            </div>
          ) : (
            codes.map((item) => (
              <CodeRow key={item.code} item={item} onDelete={handleDelete} onRevoke={handleRevoke} onExtend={handleExtend} token={adminSession.token} />
            ))
          )}
        </div>

        {/* API Calls Counter */}
        <ApiCallsPanel token={adminSession.token} />

        {/* Ricalibra Statistiche */}
        <div style={{ background: C.surface, border: `1px solid rgba(239,68,68,0.2)`, marginTop: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.85rem 1rem", borderBottom: `1px solid rgba(239,68,68,0.1)` }}>
            <DatabaseZap size={13} style={{ color: C.red }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
              Ricalibra Statistiche
            </span>
            <span style={{ marginLeft: "auto", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: C.red, padding: "0.15rem 0.5rem", border: `1px solid rgba(239,68,68,0.3)`, background: "rgba(239,68,68,0.06)" }}>
              ZONA PERICOLOSA
            </span>
          </div>
          <div style={{ padding: "1.25rem" }}>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: C.muted, marginBottom: "1rem", lineHeight: 1.6 }}>
              Rimuove dal database le statistiche di tutte le leghe <strong style={{ color: C.text }}>non presenti nella whitelist</strong> (leghe minori, femminili, giovanili e campionati non supportati).
              Questa operazione è <strong style={{ color: C.red }}>irreversibile</strong> e serve a "ricalibrare" la pagina Statistiche escludendo i dati storici delle competizioni ora escluse.
            </p>

            {cleanStatsResult && (
              <div data-testid="clean-stats-result" style={{ marginBottom: "1rem", padding: "0.85rem 1rem", background: "rgba(34,197,94,0.06)", border: `1px solid rgba(34,197,94,0.2)` }}>
                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                  {[
                    { label: "Record rimossi", value: cleanStatsResult.deleted, color: C.red },
                    { label: "Record rimanenti", value: cleanStatsResult.remaining, color: C.green },
                    { label: "Totale prima", value: cleanStatsResult.total_before, color: C.muted },
                  ].map((s) => (
                    <div key={s.label}>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: "1.4rem", color: s.color }}>{s.value}</div>
                      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.65rem", color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: C.green, marginTop: "0.6rem" }}>{cleanStatsResult.message}</p>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <AlertTriangle size={13} style={{ color: C.yellow, flexShrink: 0 }} />
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: C.muted }}>
                Solo i record con un <span style={{ color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>league_id</span> non in whitelist vengono eliminati.
              </span>
            </div>

            <button
              data-testid="clean-stats-btn"
              onClick={handleCleanStats}
              disabled={cleaningStats}
              style={{
                marginTop: "1rem", padding: "0.65rem 1.25rem",
                background: cleaningStats ? "rgba(239,68,68,0.1)" : "transparent",
                color: C.red, border: `1px solid rgba(239,68,68,0.4)`,
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.85rem", letterSpacing: "0.12em", textTransform: "uppercase",
                cursor: cleaningStats ? "wait" : "pointer",
                display: "flex", alignItems: "center", gap: "0.5rem",
              }}
              onMouseEnter={(e) => { if (!cleaningStats) e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
              onMouseLeave={(e) => { if (!cleaningStats) e.currentTarget.style.background = "transparent"; }}
            >
              {cleaningStats
                ? <><Loader2 size={13} style={{ animation: "spin 0.8s linear infinite" }} /> Pulizia in corso...</>
                : <><DatabaseZap size={13} /> Pulisci Statistiche Leghe Escluse</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
